import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from 'react-dom';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LogoTile from './components/LogoTile';
import ProgressBar from './components/ProgressBar';
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import { Progress } from "./components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Timer, Play, Pause, Square, Target, Trophy, Calendar, Smartphone, Settings, LogIn, User, CheckCircle2, Circle, X, Edit, Plus, Trash2, ChevronUp, ChevronDown, FileCheck, Undo } from "lucide-react";
import EditableTaskList from "./components/EditableTaskList";
import TactixAIChat from "./components/TactixAIChat";
import { computeSprintMetrics } from "./lib/sprintMetrics";
import { getLatestSessionWithNextMove } from "./lib/nextMove";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// User Identifier Management
const getUserId = () => {
  let userId = localStorage.getItem('timetactix_user_id');
  if (!userId) {
    // Generate a stable user ID based on timestamp and random values
    userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('timetactix_user_id', userId);
  }
  return userId;
};

// Private API wrapper that includes user ID in all requests
const privateAPI = {
  async get(endpoint) {
    const userId = getUserId();
    const url = `${API}${endpoint}${endpoint.includes('?') ? '&' : '?'}user_id=${userId}`;
    const token = localStorage.getItem('authToken');
    return await axios.get(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  },
  
  async post(endpoint, data = {}) {
    const userId = getUserId();
    const token = localStorage.getItem('authToken');
    return await axios.post(`${API}${endpoint}`, {
      ...data,
      user_id: userId
    }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  },
  
  async put(endpoint, data = {}) {
    const userId = getUserId();
    const token = localStorage.getItem('authToken');
    return await axios.put(`${API}${endpoint}`, {
      ...data,
      user_id: userId
    }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  },
  
  async delete(endpoint) {
    const userId = getUserId();
    const url = `${API}${endpoint}${endpoint.includes('?') ? '&' : '?'}user_id=${userId}`;
    const token = localStorage.getItem('authToken');
    return await axios.delete(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  },
  
  async patch(endpoint, data = {}) {
    const userId = getUserId();
    const token = localStorage.getItem('authToken');
    return await axios.patch(`${API}${endpoint}`, {
      ...data,
      user_id: userId
    }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  }
};

// Auth Context
const AuthContext = React.createContext({});

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored auth token
    const token = localStorage.getItem('authToken');
    if (token) {
      // Validate token and get user info
      validateToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const validateToken = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('authToken');
    } finally {
      setLoading(false);
    }
  };

  const login = async (userData = {}) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: userData.username || 'demo_user',
        email: userData.email || 'demo@timetactix.com'
      });
      
      const { access_token, user: authUser } = response.data;
      localStorage.setItem('authToken', access_token);
      setUser(authUser);
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
  };

  return { user, login, logout, loading };
};

// Drift-free, monotonic TimerService using performance.now() and RAF
class TimerService {
  constructor() {
    this.remainingMs = 0;
    this.isPaused = true;
    this.hasStarted = false;
    this.isRunning = false;
    this.completed = false;
    this.lastTickAtMs = 0;
    this.tickId = null;
    this.renderSeconds = null;
    this.breakConfig = null;
    this.onComplete = null;
    this.onUpdate = null;
    this.sprintId = null;
    this.stateChangeListeners = new Set();
    this.resumeInProgress = false; // Flag to prevent race conditions during resume
    this.pauseReason = null; // Track why timer was paused ('break', 'manual', etc.)
    
    // Initialize Break Test Mode detection
    this.initBreakTestMode();
  }

  // Initialize and detect Break Test Mode from URL
  initBreakTestMode() {
    const urlSearch = window.location.search;
    const urlHref = window.location.href;
    const flagFromSearch = new URLSearchParams(urlSearch).has('ttxBreakTest');
    const flagFromHref = urlHref.includes('ttxBreakTest=1');
    
    // Session-scoped flag (survives navigation)
    if (!this.breakTestMode) {
      this.breakTestMode = flagFromSearch || flagFromHref || sessionStorage.getItem('ttxBreakTestMode') === 'true';
    }
    
    if (this.breakTestMode) {
      sessionStorage.setItem('ttxBreakTestMode', 'true');
      console.log("Break Test Mode ON (1min → 1s)");
    } else {
      sessionStorage.removeItem('ttxBreakTestMode');
    }
    
    // Always expose test helpers globally (useful in all modes)
    this.setupTestHelpers();
    
    // Print diagnostic probe
    this.printBreakTestProbe();
  }

  // Print comprehensive diagnostic information
  printBreakTestProbe() {
    const activeSprint = this.getCurrentActiveSprint?.();
    const breakConfig = this.breakConfig || activeSprint?.break_config;
    
    console.log("Break Test Probe", {
      currentURL: window.location.href,
      detectedFlagFromSearch: new URLSearchParams(window.location.search).has('ttxBreakTest'),
      detectedFlagFromHref: window.location.href.includes('ttxBreakTest=1'),
      breakTestMode: this.breakTestMode,
      windowTTXExists: typeof window.TTX !== 'undefined',
      hasActiveSprint: !!activeSprint,
      sprintBreakConfig: breakConfig,
      breaksFired: activeSprint?.breaksFired || [],
      remainingMs: this.remainingMs,
      durationMs: activeSprint?.durationMs || 0,
      elapsedMs: activeSprint?.durationMs ? (activeSprint.durationMs - this.remainingMs) : 0,
      sessionFlag: sessionStorage.getItem('ttxBreakTestMode')
    });
  }

  // Setup test helpers
  setupTestHelpers() {
    window.TTX = {
      fastForward: (ms) => this.fastForwardHelper(ms),
      forceBreakNow: () => this.forceBreakNowHelper(),
      forceCompleteNow: () => this.forceCompleteNowHelper()
    };
  }

  // Cleanup test helpers
  cleanupTestHelpers() {
    if (window.TTX) {
      delete window.TTX;
    }
  }

  // Add state change listener
  addStateChangeListener(callback) {
    this.stateChangeListeners.add(callback);
  }

  // Remove state change listener
  removeStateChangeListener(callback) {
    this.stateChangeListeners.delete(callback);
  }

  // Emit state change to all listeners
  emitStateChange() {
    const state = this.getState();
    this.stateChangeListeners.forEach(callback => {
      try {
        callback(state);
      } catch (error) {
        console.error('TimerService state change listener error:', error);
      }
    });
  }

  // Global helper - fast forward timer by specified milliseconds
  fastForwardHelper(ms) {
    const activeSprint = this.getCurrentActiveSprint?.();
    if (!activeSprint || this.completed) {
      console.warn("fastForward requires an active sprint");
      return;
    }
    
    // Safely reduce remainingMs (not below zero)
    this.remainingMs = Math.max(0, this.remainingMs - ms);
    
    // Update activeSprint in memory (don't persist test changes to storage)
    activeSprint.remainingMs = this.remainingMs;
    
    // Immediately run break check
    this.checkScheduledBreaks();
    
    console.log(`FastForward: reduced by ${ms}ms, remaining: ${this.remainingMs}ms`);
  }

  // Global helper - force next break immediately
  forceBreakNowHelper() {
    const activeSprint = this.getCurrentActiveSprint?.();
    if (!activeSprint || this.completed) {
      console.warn("forceBreakNow requires an active sprint");
      return;
    }
    
    const breakConfig = this.breakConfig || activeSprint.break_config;
    if (!breakConfig || !breakConfig.enabled) {
      console.log("Breaks disabled: no fire");
      return;
    }
    
    const breaksFired = activeSprint.breaksFired || [];
    const breakOffsets = (breakConfig.break_times || []).map(timeMinutes => {
      const normalOffsetMs = timeMinutes * 60 * 1000;
      return this.breakTestMode ? normalOffsetMs / 60 : normalOffsetMs;
    });
    
    // Find next pending break
    const nextOffset = breakOffsets.find(offset => !breaksFired.includes(offset));
    if (!nextOffset) {
      console.log("No pending breaks to fire");
      return;
    }
    
    // Ensure fresh state for manual trigger - reset any stale flags
    console.log(`ForceBreakNow: Resetting stale state for sprint ${this.sprintId}, triggering break at ${nextOffset}ms`);
    
    // Add to breaksFired and persist
    const updatedBreaksFired = [...breaksFired, nextOffset];
    activeSprint.breaksFired = updatedBreaksFired;
    
    // Log break trigger with remainingMs before pause
    console.log(`BreakTrigger Verification with {sprintId: ${this.sprintId}, offsetMs: ${nextOffset}, remainingMs_beforePause: ${this.remainingMs}}`);
    
    // Pause timer atomically FIRST - this is critical for the break flow
    this.pauseAtomic('break');
    
    // Persist activeSprint
    this.persistActiveSprint?.(activeSprint);
    
    // Dispatch break UI event after atomic pause - exactly follow natural break flow
    window.dispatchEvent(new CustomEvent('ttx:break-start', {
      detail: { 
        sprintId: this.sprintId, 
        offsetMs: nextOffset, 
        testMode: this.breakTestMode 
      }
    }));
    console.log(`Break UI Event Dispatched with {sprintId: ${this.sprintId}, offsetMs: ${nextOffset}}`);
    
    console.log(`ForceBreakNow: fired break at ${nextOffset}ms offset`);
  }

  // Global helper - force sprint completion immediately
  forceCompleteNowHelper() {
    const activeSprint = this.getCurrentActiveSprint?.();
    if (!activeSprint || this.completed) {
      console.debug('[COMPLETE] forceCompleteNow() → no active sprint');
      return;
    }
    
    // Check if already completed to ensure idempotency
    if (this.remainingMs === 0 || this.completed) {
      console.debug('[COMPLETE] forceCompleteNow() → sprint already completed');
      return;
    }
    
    console.debug(`[COMPLETE] forceCompleteNow() → forcing completion for sprint ${this.sprintId}`);
    
    // Set timer to finished state - exactly as natural completion would
    this.remainingMs = 0;
    this.isPaused = true;
    this.isRunning = false;
    this.completed = true;
    this.stopLoop();
    
    console.log('TIMER_COMPLETED (forced)', { 
      sprintId: this.sprintId,
      completed: this.completed 
    });
    
    // Dispatch sprint completion event - same as natural flow
    window.dispatchEvent(new CustomEvent('ttx:sprint-complete', {
      detail: { 
        sprintId: this.sprintId
      }
    }));
    console.debug('[COMPLETE] forceCompleteNow() → completion modal opened');
    
    // Call completion callback if provided
    if (this.onComplete) {
      this.onComplete();
    }
    
    // Emit state change
    this.emitStateChange();
  }

  // Check for scheduled breaks during tick
  checkScheduledBreaks() {
    // Skip if no break config or during resume transaction
    if (!this.breakConfig || this.resumeInProgress) {
      return;
    }
    
    // Access activeSprint from global state
    const activeSprint = this.getCurrentActiveSprint?.();
    if (!activeSprint || !activeSprint.durationMs) {
      return;
    }
    
    // Check if breaks are enabled
    const breakConfig = this.breakConfig || activeSprint.break_config;
    if (!breakConfig || !breakConfig.enabled) {
      console.log("Breaks disabled: no fire");
      return;
    }
    
    const durationMs = activeSprint.durationMs;
    const elapsedMs = durationMs - this.remainingMs;
    const breaksFired = activeSprint.breaksFired || [];
    
    // Convert break times from minutes to milliseconds, with test mode scaling
    const breakOffsets = (breakConfig.break_times || []).map(timeMinutes => {
      const normalOffsetMs = timeMinutes * 60 * 1000;
      // In Break Test Mode: 1 minute = 1 second (60x speed)
      return this.breakTestMode ? normalOffsetMs / 60 : normalOffsetMs;
    });
    
    // Check each break offset
    for (const offsetMs of breakOffsets) {
      // Check if we've crossed this break threshold
      if (elapsedMs >= offsetMs && !breaksFired.includes(offsetMs)) {
        // Log break trigger with remainingMs before pause
        console.log(`BreakTrigger Verification with {sprintId: ${this.sprintId}, offsetMs: ${offsetMs}, remainingMs_beforePause: ${this.remainingMs}}`);
        
        // Lightweight guard: if pauseReason is null and offset already recorded, don't re-dispatch
        if (this.pauseReason === null && breaksFired.includes(offsetMs)) {
          console.log(`Break already fired for offset ${offsetMs}, skipping UI event dispatch`);
          return;
        }
        
        // Pause timer atomically FIRST
        this.pauseAtomic('break');
        
        // Add to breaksFired and persist
        const updatedBreaksFired = [...breaksFired, offsetMs];
        activeSprint.breaksFired = updatedBreaksFired;
        
        // Trigger break UI/notification (legacy - kept for compatibility)
        this.triggerBreak?.(activeSprint);
        
        // Persist activeSprint
        this.persistActiveSprint?.(activeSprint);
        
        // Dispatch break UI event after atomic pause
        window.dispatchEvent(new CustomEvent('ttx:break-start', {
          detail: { 
            sprintId: this.sprintId, 
            offsetMs: offsetMs, 
            testMode: this.breakTestMode 
          }
        }));
        console.log(`Break UI Event Dispatched with {sprintId: ${this.sprintId}, offsetMs: ${offsetMs}}`);
        
        return; // Only fire one break per tick
      }
      
      // Check if we're approaching the next break (within 2 seconds, or 2/60 seconds in test mode)
      if (breakOffsets.length > 0) {
        const nextOffsetMs = breakOffsets.find(offset => offset > elapsedMs && !breaksFired.includes(offset));
        const proximityThreshold = this.breakTestMode ? 2000 / 60 : 2000; // Scale threshold in test mode
        if (nextOffsetMs && Math.abs(elapsedMs - nextOffsetMs) <= proximityThreshold) {
          console.log("Break window crossed", {
            elapsedMs,
            nextOffsetMs,
            willFire: true,
            testMode: this.breakTestMode,
            reasonIfFalse: undefined
          });
        }
      }
    }
  }

  // Set methods to access activeSprint and trigger breaks
  setBreakHandlers(getCurrentActiveSprint, persistActiveSprint, triggerBreak) {
    this.getCurrentActiveSprint = getCurrentActiveSprint;
    this.persistActiveSprint = persistActiveSprint;
    this.triggerBreak = triggerBreak;
  }

  // Start a new sprint timer
  startNew(durationMs, sprintId, breakConfig = null, onComplete = null, onUpdate = null) {
    // Complete break state reset for new sprint
    this.resetBreakState();
    
    this.remainingMs = durationMs;
    this.isPaused = false;
    this.hasStarted = true;
    this.isRunning = true;
    this.completed = false;
    this.lastTickAtMs = performance.now();
    this.sprintId = sprintId;
    this.breakConfig = breakConfig;
    this.onComplete = onComplete;
    this.onUpdate = onUpdate;
    this.renderSeconds = null;
    this.pauseReason = null; // Clear any previous pause reason
    this.resumeInProgress = false; // Clear resume flag
    
    console.log('TIMER_START_NEW', { 
      sprintId: this.sprintId, 
      durationMs: durationMs,
      hasStarted: this.hasStarted,
      isRunning: this.isRunning,
      breakStateReset: true
    });
    
    this.startLoop();
    this.emitStateChange();
  }

  // Reset all break-related state for new sprint
  resetBreakState() {
    // Clear any break-related flags and timers
    this.pauseReason = null;
    this.resumeInProgress = false;
    
    // Clear any stale break test helpers and recreate if needed
    if (this.breakTestMode) {
      this.cleanupTestHelpers();
      this.setupTestHelpers();
    }
    
    console.log('BREAK_STATE_RESET', { 
      previousSprintId: this.sprintId,
      breakTestMode: this.breakTestMode
    });
  }

  // Hydrate from activeSprint (idempotent - no-op if state exists for same sprint)
  hydrate(activeSprintData, onComplete = null, onUpdate = null) {
    // Idempotent check - no-op if state already exists for same sprint
    if (this.sprintId === activeSprintData.id && this.remainingMs > 0) {
      console.log('HYDRATE_SKIPPED', { 
        sprintId: this.sprintId, 
        existingRemainingMs: this.remainingMs 
      });
      return;
    }
    
    this.remainingMs = activeSprintData.remainingMs;
    this.isPaused = activeSprintData.isPaused;
    this.hasStarted = activeSprintData.remainingMs < activeSprintData.durationMs || !activeSprintData.isPaused;
    this.isRunning = !activeSprintData.isPaused && activeSprintData.remainingMs > 0;
    this.completed = activeSprintData.remainingMs <= 0 && !this.resumeInProgress; // Don't complete during resume
    this.breakConfig = activeSprintData.break_config;
    this.onComplete = onComplete;
    this.onUpdate = onUpdate;
    this.sprintId = activeSprintData.id;
    this.renderSeconds = null;
    
    console.log('RESUME_HYDRATED', {
      sprintId: this.sprintId,
      remainingMs: this.remainingMs,
      hasStarted: this.hasStarted,
      isRunning: this.isRunning,
      isPaused: this.isPaused
    });
    
    if (!this.isPaused && this.remainingMs > 0) {
      this.lastTickAtMs = performance.now();
      this.startLoop();
    }
    
    this.emitStateChange();
  }

  // Pause the timer with exact remaining time
  pause() {
    if (!this.isPaused && this.tickId) {
      // Apply final delta to get exact remainingMs
      const now = performance.now();
      const delta = now - this.lastTickAtMs;
      this.remainingMs = Math.max(0, this.remainingMs - delta);
      
      this.isPaused = true;
      this.isRunning = false;
      this.pauseReason = 'manual';
      this.stopLoop();
      
      console.log('TIMER_SET_PAUSED_TRUE', { 
        sprintId: this.sprintId, 
        remainingMs: this.remainingMs,
        isRunning: this.isRunning
      });
      
      // Emit state change
      this.emitStateChange();
    }
  }

  // Atomic pause for breaks - stops timer immediately without UI delays
  pauseAtomic(reason = 'break') {
    if (!this.isPaused && this.tickId) {
      // Apply final delta to get exact remainingMs
      const now = performance.now();
      const delta = now - this.lastTickAtMs;
      this.remainingMs = Math.max(0, this.remainingMs - delta);
      
      // Set atomic pause state
      this.isPaused = true;
      this.isRunning = false;
      this.pauseReason = reason;
      this.stopLoop();
      
      // Persist activeSprint immediately before any UI work
      const activeSprint = this.getCurrentActiveSprint?.();
      if (activeSprint && this.persistActiveSprint) {
        const updatedActiveSprint = {
          ...activeSprint,
          remainingMs: this.remainingMs,
          isPaused: true,
          isRunning: false,
          pauseReason: reason,
          lastTickAt: now
        };
        this.persistActiveSprint(updatedActiveSprint);
      }
      
      console.log(`Break Pause Applied with {isPaused: ${this.isPaused}, isRunning: ${this.isRunning}, pauseReason: '${this.pauseReason}', remainingMs_afterPause: ${this.remainingMs}}`);
      
      // Emit state change
      this.emitStateChange();
    }
  }

  // Resume the timer from exact remainingMs
  resume() {
    if (this.isPaused && this.remainingMs > 0) {
      this.isPaused = false;
      this.isRunning = true;
      this.hasStarted = true; // Set hasStarted when resuming (covers first start too)
      this.pauseReason = null; // Clear pause reason
      this.lastTickAtMs = performance.now();
      this.startLoop();
      
      console.log('TIMER_SET_PAUSED_FALSE', { 
        sprintId: this.sprintId, 
        remainingMs: this.remainingMs,
        hasStarted: this.hasStarted,
        isRunning: this.isRunning
      });
      
      // Emit state change
      this.emitStateChange();
    }
  }

  // Atomic resume transaction to prevent race conditions
  resumeAtomic(activeSprintData) {
    return new Promise((resolve, reject) => {
      try {
        // Set atomic flag
        this.resumeInProgress = true;
        
        console.log('RESUME_TX_START', {
          sprintId: activeSprintData.id,
          remainingMs: activeSprintData.remainingMs,
          isPaused: activeSprintData.isPaused
        });
        
        // Verify remainingMs > 0
        if (activeSprintData.remainingMs <= 0) {
          this.resumeInProgress = false;
          reject(new Error('Invalid remainingMs'));
          return;
        }
        
        // Update sprint state - set to running
        const updatedSprintData = {
          ...activeSprintData,
          isPaused: false,
          lastTickAt: performance.now()
          // remainingMs preserved
        };
        
        // Hydrate if no state exists, reuse existing state otherwise
        if (!this.sprintId || this.sprintId !== activeSprintData.id || this.remainingMs <= 0) {
          this.hydrate(updatedSprintData);
        } else {
          // Update existing state to running
          this.isPaused = false;
          this.isRunning = true;
          this.hasStarted = true;
          this.lastTickAtMs = performance.now();
        }
        
        // Start RAF loop if not running
        if (!this.tickId) {
          this.startLoop();
          console.log('RESUME_LOOP_RUNNING', { tickId: !!this.tickId });
        }
        
        // Clear atomic flag
        this.resumeInProgress = false;
        
        // Emit state change after transaction complete
        this.emitStateChange();
        
        console.log('RESUME_TX_END', {
          sprintId: this.sprintId,
          remainingMs: this.remainingMs,
          isRunning: this.isRunning,
          isPaused: this.isPaused
        });
        
        resolve(updatedSprintData);
        
      } catch (error) {
        this.resumeInProgress = false;
        reject(error);
      }
    });
  }

  // Stop the timer completely
  stop() {
    if (this.resumeInProgress) return; // Ignore during resume transaction
    
    this.stopLoop();
    this.isPaused = true;
    this.isRunning = false;
    this.completed = true;
    
    console.log('TIMER_STOPPED', { 
      sprintId: this.sprintId,
      completed: this.completed 
    });
    
    this.emitStateChange();
  }

  // Start the RAF loop (ensure only one is active)
  startLoop() {
    if (this.tickId) {
      // Already running, don't start another
      return;
    }
    
    const tick = () => {
      const now = performance.now();
      const delta = now - this.lastTickAtMs;
      this.lastTickAtMs = now;
      
      if (!this.isPaused) {
        // Apply Break Test Mode multiplier to elapsed time (1 min = 1 sec)
        const effectiveDelta = this.breakTestMode ? delta * 60 : delta;
        this.remainingMs = Math.max(0, this.remainingMs - effectiveDelta);
        
        // Check for scheduled breaks (tick-based, persisted)
        this.checkScheduledBreaks();
        
        // Check for completion - underflow-safe, debounced via completed flag
        if (this.remainingMs <= 0 && !this.resumeInProgress && !this.completed) {
          this.isPaused = true;
          this.isRunning = false;
          this.completed = true;
          this.stopLoop();
          
          console.log('TIMER_COMPLETED', { 
            sprintId: this.sprintId,
            completed: this.completed 
          });
          
          // Dispatch sprint completion event for modal
          window.dispatchEvent(new CustomEvent('ttx:sprint-complete', {
            detail: { 
              sprintId: this.sprintId
            }
          }));
          console.log('SPRINT_COMPLETE_EVENT_DISPATCHED', { sprintId: this.sprintId });
          
          if (this.onComplete) {
            this.onComplete();
          }
          this.emitStateChange();
          return;
        }
      }
      
      // Update display only when seconds change (avoid jitter)
      const displaySeconds = Math.ceil(this.remainingMs / 1000);
      if (displaySeconds !== this.renderSeconds) {
        this.renderSeconds = displaySeconds;
        if (this.onUpdate) {
          this.onUpdate({
            timeLeft: displaySeconds,
            remainingMs: this.remainingMs,
            isPaused: this.isPaused,
            hasStarted: this.hasStarted,
            isRunning: this.isRunning,
            completed: this.completed
          });
        }
      }
      
      if (!this.isPaused) {
        this.tickId = requestAnimationFrame(tick);
      }
    };
    
    this.tickId = requestAnimationFrame(tick);
  }

  // Stop the RAF loop
  stopLoop() {
    if (this.tickId) {
      cancelAnimationFrame(this.tickId);
      this.tickId = null;
    }
  }

  // Get current state
  getState() {
    return {
      timeLeft: Math.ceil(this.remainingMs / 1000),
      remainingMs: this.remainingMs,
      isPaused: this.isPaused,
      hasStarted: this.hasStarted,
      isRunning: this.isRunning,
      completed: this.completed,
      lastTickAtMs: this.lastTickAtMs,
      pauseReason: this.pauseReason
    };
  }

  // Format time for display
  formatTime(totalSeconds = null) {
    const seconds = totalSeconds !== null ? totalSeconds : Math.ceil(this.remainingMs / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}

// Create singleton instance
const timerService = new TimerService();

// Hook to use TimerService in React components
const useTimerService = (sprint, activeSprint) => {
  const [timerState, setTimerState] = useState(null);

  // State change listener for TimerService
  const handleStateChange = useCallback((state) => {
    setTimerState(state);
    console.log('TIMER_UI_SYNC_OK', { 
      hasStarted: state.hasStarted,
      isRunning: state.isRunning,
      isPaused: state.isPaused, 
      timeLeft: state.timeLeft,
      remainingMs: state.remainingMs
    });
  }, []);

  // Only read TimerService state and subscribe - NO defaults, NO hydration on mount
  useEffect(() => {
    // Read current TimerService state
    const currentTimerState = timerService.getState();
    
    if (currentTimerState && currentTimerState.remainingMs > 0) {
      // TimerService has valid state - use it
      setTimerState(currentTimerState);
      console.log('TIMER_STATE', {
        hasStarted: currentTimerState.hasStarted,
        isRunning: currentTimerState.isRunning,
        isPaused: currentTimerState.isPaused,
        remainingMs: currentTimerState.remainingMs
      });
    } else if (activeSprint && activeSprint.remainingMs > 0 && !timerService.resumeInProgress) {
      // Only hydrate if resumeInProgress is false and TimerService truly has no state
      timerService.hydrate(activeSprint);
      const hydratedState = timerService.getState();
      setTimerState(hydratedState);
      
      console.log('TIMER_STATE', {
        hasStarted: hydratedState.hasStarted,
        isRunning: hydratedState.isRunning,
        isPaused: hydratedState.isPaused,
        remainingMs: hydratedState.remainingMs
      });
    }
    // If neither condition is met, leave timerState as null (no defaults)
    
    // Subscribe to changes
    timerService.addStateChangeListener(handleStateChange);
    
    return () => {
      // Unsubscribe on unmount
      timerService.removeStateChangeListener(handleStateChange);
    };
  }, [handleStateChange, activeSprint?.id]); // Only depend on activeSprint id

  // Guard: rebind if UI shows 00:00 while TimerService has remainingMs > 0
  useEffect(() => {
    if (timerState && timerState.timeLeft === 0 && timerState.remainingMs === 0) {
      const serviceState = timerService.getState();
      if (serviceState && serviceState.remainingMs > 0) {
        console.log('RESUME_REBIND_FIXED', {
          uiRemainingMs: timerState.remainingMs,
          serviceRemainingMs: serviceState.remainingMs
        });
        setTimerState(serviceState);
      }
    }
  }, [timerState?.timeLeft, timerState?.remainingMs]);

  const startNew = (durationMs, activeSprintData) => {
    const onComplete = () => {
      // Timer completed - handled by parent
    };
    
    timerService.startNew(
      durationMs, 
      activeSprintData.id, 
      activeSprintData.break_config,
      onComplete,
      (state) => setTimerState(state)
    );
  };

  const hydrate = (activeSprintData) => {
    timerService.hydrate(activeSprintData);
    const currentState = timerService.getState();
    setTimerState(currentState);
  };

  // Return timerState directly or safe defaults only for rendering
  const safeState = timerState || {
    timeLeft: 0,
    remainingMs: 0,
    isPaused: true,
    hasStarted: false,
    isRunning: false,
    completed: false
  };

  return {
    timeLeft: safeState.timeLeft,
    remainingMs: safeState.remainingMs,
    isPaused: safeState.isPaused,
    hasStarted: safeState.hasStarted,
    isRunning: safeState.isRunning,
    completed: safeState.completed,
    startNew,
    hydrate,
    pauseTimer: () => timerService.pause(),
    resumeTimer: () => timerService.resume(),
    start: () => timerService.resume(),
    pause: () => timerService.pause(),
    resume: () => timerService.resume(),
    stop: () => timerService.stop(),
    getState: () => timerService.getState(),
    formatTime: (seconds) => timerService.formatTime(seconds)
  };
};

// Timer Hook with Focus Mode and Break Support (renamed to avoid conflicts)
const useTimerInternal = (initialTime, breakConfig = null) => {
  const [timeLeft, setTimeLeft] = useState(initialTime * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [focusStartTime, setFocusStartTime] = useState(null);
  const [totalFocusedTime, setTotalFocusedTime] = useState(0);
  
  // Break system state
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakTimeLeft, setBreakTimeLeft] = useState(0);
  const [showBreakAlert, setShowBreakAlert] = useState(false);
  const [showBreakEndAlert, setShowBreakEndAlert] = useState(false);
  const [completedBreaks, setCompletedBreaks] = useState([]);
  const [breakAlertCountdown, setBreakAlertCountdown] = useState(30); // 30 second countdown
  
  const intervalRef = useRef(null);
  const breakEndTimeoutRef = useRef(null);
  const breakAlertTimeoutRef = useRef(null);

  // Check if it's time for a break
  const checkForBreak = (currentTimeLeft) => {
    if (!breakConfig || !breakConfig.break_times || isOnBreak) return;
    
    const elapsedMinutes = initialTime - (currentTimeLeft / 60);
    
    for (const breakTime of breakConfig.break_times) {
      if (!completedBreaks.includes(breakTime) && elapsedMinutes >= breakTime) {
        setShowBreakAlert(true);
        setBreakAlertCountdown(30); // Reset countdown to 30 seconds
        setIsPaused(true);
        if (focusStartTime) {
          setTotalFocusedTime(prev => prev + (Date.now() - focusStartTime));
          setFocusStartTime(null);
        }
        setCompletedBreaks(prev => [...prev, breakTime]);
        break;
      }
    }
  };

  // Auto-skip break function (used by timeout) - removed for SprintTimer atomic behavior
  // This is legacy from useTimerInternal

  useEffect(() => {
    if (isRunning && !isPaused && !isOnBreak) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          const newTimeLeft = prev - 1;
          if (newTimeLeft <= 1) {
            setIsRunning(false);
            return 0;
          }
          
          // Check for breaks
          checkForBreak(newTimeLeft);
          
          return newTimeLeft;
        });
      }, 1000);
    } else if (isOnBreak) {
      // Break timer countdown
      intervalRef.current = setInterval(() => {
        setBreakTimeLeft((prev) => {
          if (prev <= 1) {
            // Break is over
            setIsOnBreak(false);
            setShowBreakEndAlert(true);
            
            // Auto-close break end alert after 8 seconds
            breakEndTimeoutRef.current = setTimeout(() => {
              setShowBreakEndAlert(false);
            }, 8000);
            
            // Auto-resume work session
            setIsPaused(false);
            setFocusStartTime(Date.now());
            
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (showBreakAlert && breakAlertCountdown > 0) {
      // Break alert countdown timer - removed for atomic behavior
      intervalRef.current = setInterval(() => {
        setBreakAlertCountdown((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => {
      clearInterval(intervalRef.current);
      if (breakEndTimeoutRef.current) {
        clearTimeout(breakEndTimeoutRef.current);
      }
    };
  }, [isRunning, isPaused, isOnBreak, showBreakAlert, breakAlertCountdown, focusStartTime]);

  const start = () => {
    setIsRunning(true);
    setIsPaused(false);
    if (!focusStartTime) {
      setFocusStartTime(Date.now());
    }
  };

  const pause = () => {
    setIsPaused(true);
    if (focusStartTime) {
      setTotalFocusedTime(prev => prev + (Date.now() - focusStartTime));
      setFocusStartTime(null);
    }
  };

  const resume = () => {
    setIsPaused(false);
    setFocusStartTime(Date.now());
  };

  const stop = () => {
    setIsRunning(false);
    setIsPaused(false);
    setIsOnBreak(false);
    setShowBreakAlert(false);
    setBreakAlertCountdown(30);
    if (focusStartTime) {
      setTotalFocusedTime(prev => prev + (Date.now() - focusStartTime));
      setFocusStartTime(null);
    }
    setTimeLeft(initialTime * 60);
  };

  // Break control functions
  const startBreak = () => {
    setShowBreakAlert(false);
    setBreakAlertCountdown(30);
    setIsOnBreak(true);
    setBreakTimeLeft((breakConfig?.break_duration || 5) * 60); // Convert to seconds
  };

  const skipBreak = () => {
    setShowBreakAlert(false);
    setBreakAlertCountdown(30);
    setIsPaused(false);
    setFocusStartTime(Date.now());
  };

  const closeBreakEndAlert = () => {
    setShowBreakEndAlert(false);
    if (breakEndTimeoutRef.current) {
      clearTimeout(breakEndTimeoutRef.current);
    }
  };

  const getElapsedTime = () => {
    let elapsed = totalFocusedTime;
    if (focusStartTime && isRunning && !isPaused) {
      elapsed += Date.now() - focusStartTime;
    }
    return Math.floor(elapsed / 1000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Method to set remaining time (used for resume functionality)
  const setRemainingTime = (seconds) => {
    setTimeLeft(seconds);
  };

  return {
    timeLeft,
    isRunning,
    isPaused,
    isOnBreak,
    breakTimeLeft,
    showBreakAlert,
    showBreakEndAlert,
    breakAlertCountdown,
    start,
    pause,
    resume,
    stop,
    setTimeLeft: setRemainingTime,
    startBreak,
    skipBreak,
    closeBreakEndAlert,
    getElapsedTime,
    formatTime: () => formatTime(timeLeft),
    formatBreakTime: () => formatTime(breakTimeLeft),
    formatBreakAlertCountdown: () => formatTime(breakAlertCountdown),
    formatElapsed: () => formatTime(getElapsedTime()),
    progress: ((initialTime * 60 - timeLeft) / (initialTime * 60)) * 100
  };
};

// Analytics helper
const trackEvent = (eventName, properties = {}) => {
  // Simple analytics - in production, this would send to your analytics service
  console.log('Analytics:', eventName, properties);
  // Example: analytics.track(eventName, properties);
};

// Utility function to format milliseconds as MM:SS or H:MM:SS
const formatMs = (ms) => {
  const totalSeconds = Math.ceil(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
};

// British DD/MM/YYYY date helpers for Sprint Journal
const formatJournalDate = (input) => {
  if (!input) return '';
  const d = new Date(input);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB'); // DD/MM/YYYY
};

const formatJournalDateTime = (input) => {
  if (!input) return '';
  const d = new Date(input);
  if (isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('en-GB')}, ${d.toLocaleTimeString('en-GB')}`;
};

// Focus Mode Component
const FocusMode = ({ 
  sprint, 
  timer, 
  tasks, 
  currentTask, 
  onTaskComplete, 
  onTaskUndo, 
  onTasksUpdate = null,
  onCurrentTaskChange = null,
  isTimerRunning = false,
  onComplete, 
  onExit, 
  onExitFocus 
}) => {
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [focusStarted, setFocusStarted] = useState(false);
  const [showTaskList, setShowTaskList] = useState(false);

  // Get most recently completed task (for undo display)
  const getRecentlyCompletedTask = () => {
    const completedTasks = tasks.filter(task => task.status === 'completed' && task.completedAt);
    if (completedTasks.length === 0) return null;
    
    // Sort by completion time and get the most recent
    return completedTasks.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))[0];
  };

  const recentlyCompletedTask = getRecentlyCompletedTask();

  useEffect(() => {
    // Try to enter fullscreen
    const enterFullscreen = async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (e) {
        // Silently ignore fullscreen errors
      }
    };
    enterFullscreen();

    // Track focus started (only once)
    if (!focusStarted) {
      trackEvent('focus_started', {
        timestamp: new Date().toISOString(),
        planned_minutes: sprint.duration_minutes
      });
      setFocusStarted(true);
    }

    return () => {
      // Exit fullscreen
      if (document.exitFullscreen && document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  // Check if timer completed - DON'T auto-navigate, let completion modal handle it
  useEffect(() => {
    if (timer.timeLeft === 0 && timer.isRunning === false && !isCompleted) {
      // Track analytics but DON'T call handleComplete (which navigates away)
      // The completion modal will show via the ttx:sprint-complete event
      // User can then manually click "Complete Sprint" after dismissing the modal
      const timerState = timer.getState();
      const durationMs = sprint.duration_minutes * 60 * 1000;
      const remainingMs = timerState.remainingMs;
      const elapsedMs = Math.max(0, durationMs - remainingMs);
      const focusedSeconds = Math.ceil(elapsedMs / 1000);
      
      trackEvent('focus_completed', {
        focused_seconds: focusedSeconds,
        completed: true
      });
      
      // DO NOT set isCompleted=true or call handleComplete - that navigates away
      // The completion modal will appear, and user must click Complete Sprint button
      // Keep showing Focus Mode UI with timer at 0:00
    }
  }, [timer.timeLeft, timer.isRunning]);

  const handleEnd = () => {
    setShowEndDialog(true);
  };

  const handleComplete = (completed = false) => {
    // Freeze timer state and apply final tick if running
    const timerState = timer.getState();
    
    // Compute elapsed time from TimerService state
    const durationMs = sprint.duration_minutes * 60 * 1000;
    const remainingMs = timerState.remainingMs;
    const elapsedMs = Math.max(0, durationMs - remainingMs);
    const focusedSeconds = Math.ceil(elapsedMs / 1000);
    
    trackEvent('focus_completed', {
      focused_seconds: focusedSeconds,
      completed: completed
    });

    timer.stop();
    onComplete({ ...sprint, tasks }, focusedSeconds, completed);
  };

  const handleExitFocus = () => {
    // Track exit without ending
    trackEvent('focus_exited_without_ending', {
      timestamp: new Date().toISOString(),
      remaining_seconds: timer.timeLeft
    });

    // Exit fullscreen
    if (document.exitFullscreen && document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }

    onExitFocus();
  };

  const handleContinue = () => {
    setShowEndDialog(false);
  };

  if (isCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-8">
        <div className="text-center space-y-8 max-w-md">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-3xl font-bold text-white">Sprint complete</h1>
          <div className="space-y-2 text-slate-300">
            <p>Focused time: {formatMs(Math.max(0, (sprint.duration_minutes * 60 * 1000) - timer.getState().remainingMs))}</p>
            <p>Goal: {sprint.goal}</p>
          </div>
          <div className="flex space-x-4">
            <Button 
              onClick={() => onExit('new')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              Start another
            </Button>
            <Button onClick={() => onExit('dashboard')} variant="ghost" className="text-white">
              Back to dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Status Bar */}
      <div className="text-center py-4 bg-slate-800/30">
        <p className="text-green-400 font-medium">
          🟢 Sprint in progress — {timer.formatTime()}
        </p>
      </div>

      {/* Main Timer */}
      <div className="flex-1 flex flex-col items-center justify-center space-y-6 sm:space-y-8 p-4 sm:p-8">
        <div className="text-6xl sm:text-8xl font-mono font-bold text-white text-center">
          {timer.formatTime()}
        </div>
        
        {/* Unified Progress Bar - always left-to-right fill */}
        <ProgressBar 
          totalMs={sprint.duration_minutes * 60 * 1000}
          elapsedMs={sprint.duration_minutes * 60 * 1000 - timer.remainingMs}
          isPaused={timer.isPaused}
          className="w-full max-w-sm sm:max-w-md"
        />
        
        {timer.isPaused && (
          <div className="text-amber-400 font-medium text-center">⏸️ Paused</div>
        )}
        
        <div className="text-center max-w-3xl space-y-4 px-4">
          <p className="text-lg sm:text-xl text-slate-300 break-words">
            Goal: {sprint.goal}
          </p>
          
          {/* Show recently completed task with undo option */}
          {recentlyCompletedTask && !currentTask && (
            <div className="space-y-3 p-4 bg-green-900/20 rounded-lg border border-green-700/30">
              <p className="text-lg text-green-400">
                ✓ Completed: {recentlyCompletedTask.title}
                {recentlyCompletedTask.estimated_minutes && ` (${recentlyCompletedTask.estimated_minutes} min)`}
              </p>
              <button
                onClick={() => onTaskUndo(recentlyCompletedTask.id)}
                className="text-sm text-slate-400 hover:text-white underline transition-colors"
              >
                Undo
              </button>
            </div>
          )}
          
          {/* Show current task */}
          {currentTask && (
            <div className="space-y-4">
              <p className="text-lg text-slate-400">
                Current: {currentTask.title}
                {currentTask.estimated_minutes && ` (${currentTask.estimated_minutes} min)`}
              </p>
              {currentTask.description && (
                <p className="text-sm text-slate-500 max-w-2xl mx-auto">
                  {currentTask.description}
                </p>
              )}
              <Button
                onClick={() => onTaskComplete(currentTask.id)}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark Complete
              </Button>
              
              {/* Show undo for recently completed task if current task exists */}
              {recentlyCompletedTask && (
                <div className="pt-2 border-t border-slate-600">
                  <p className="text-sm text-green-400 mb-2">
                    ✓ {recentlyCompletedTask.title}
                  </p>
                  <button
                    onClick={() => onTaskUndo(recentlyCompletedTask.id)}
                    className="text-sm text-slate-400 hover:text-white underline transition-colors"
                  >
                    Undo
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* No tasks remaining */}
          {!currentTask && !recentlyCompletedTask && (
            <div className="space-y-4">
              <p className="text-lg text-green-400">
                🎉 All tasks completed!
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="text-center pb-8 px-4">
        <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-4 max-w-4xl mx-auto">
          {/* Primary Controls Row */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-3 w-full max-w-sm">
            {timer.isPaused ? (
              <Button 
                type="button"
                onClick={timer.resume}
                className="bg-green-600 hover:bg-green-700 text-white border-0 px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-base w-full sm:flex-1"
              >
                Resume
              </Button>
            ) : (
              <Button 
                type="button"
                onClick={timer.pause}
                className="bg-amber-600 hover:bg-amber-700 text-white border-0 px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-base w-full sm:flex-1"
              >
                Pause
              </Button>
            )}
            <Button 
              onClick={handleEnd}
              className="bg-red-600 hover:bg-red-700 text-white border-0 px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-base w-full sm:flex-1"
            >
              Complete Sprint
            </Button>
          </div>
          
          {/* Secondary Controls Row */}
          <div className="flex space-x-3">
            <Button 
              onClick={handleExitFocus}
              variant="outline"
              className="bg-slate-700/50 border-slate-600 text-white hover:bg-slate-600 px-3 py-2 text-sm sm:px-6 sm:py-3 sm:text-base"
            >
              Exit Focus
            </Button>
            {onTasksUpdate && (
              <Button 
                onClick={() => setShowTaskList(true)}
                variant="outline"
                className="bg-slate-700/50 border-slate-600 text-white hover:bg-slate-600 px-3 py-2 text-sm sm:px-6 sm:py-3 sm:text-base"
              >
                Edit Tasks
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Task List Dialog */}
      {showTaskList && onTasksUpdate && (
        <Dialog open={showTaskList} onOpenChange={setShowTaskList}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Tasks</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <EditableTaskList
                tasks={tasks}
                onTasksUpdate={onTasksUpdate}
                currentTaskId={currentTask?.id}
                onCurrentTaskChange={onCurrentTaskChange}
                sprintId={sprint.id}
                isTimerRunning={isTimerRunning}
                onTaskComplete={onTaskComplete}
                onTaskUndo={onTaskUndo}
              />
              <div className="flex justify-end">
                <Button 
                  onClick={() => setShowTaskList(false)}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  Done
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* End Confirmation Dialog */}
      {showEndDialog && (
        <Dialog open={showEndDialog} onOpenChange={setShowEndDialog}>
          <DialogContent
            data-testid="focus-complete-sprint-confirm-dialog"
            className="bg-slate-800 border-slate-700"
          >
            <DialogHeader>
              <DialogTitle className="text-white">Complete sprint?</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-300">
                You've focused for {formatMs(Math.max(0, (sprint.duration_minutes * 60 * 1000) - timer.getState().remainingMs))}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  data-testid="focus-complete-sprint-confirm-button"
                  onClick={() => handleComplete(false)}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 w-full sm:w-auto"
                >
                  Complete & Save
                </Button>
                <Button
                  data-testid="focus-complete-sprint-cancel-button"
                  onClick={handleContinue}
                  variant="ghost"
                  className="text-white w-full sm:w-auto"
                >
                  Continue
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Session Detail Modal Component
const SessionDetailModal = ({ session, isOpen, onClose, onUpdateSession, onToggleStatus }) => {
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [lastSavedSessionId, setLastSavedSessionId] = useState(null);
  const [lastSavedNotes, setLastSavedNotes] = useState('');

  // Structured Win / Next Move editors (only used when session has structured fields)
  const [winDraft, setWinDraft] = useState('');
  const [nextMoveDraft, setNextMoveDraft] = useState('');
  const [isSavingStructured, setIsSavingStructured] = useState(false);
  const [structuredSaveError, setStructuredSaveError] = useState(null);

  // Inline goal-edit state (modal only)
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [goalDraft, setGoalDraft] = useState('');
  const [isSavingGoal, setIsSavingGoal] = useState(false);
  const [goalSaveError, setGoalSaveError] = useState(null);

  // Subtask completion state (modal only)
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const [undoingTaskId, setUndoingTaskId] = useState(null);

  // Mood picker state (modal only)
  const [isMoodPickerOpen, setIsMoodPickerOpen] = useState(false);
  const moodPickerRef = useRef(null);

  // Close mood picker on outside click
  useEffect(() => {
    if (!isMoodPickerOpen) return;
    const handleClickOutside = (e) => {
      if (moodPickerRef.current && !moodPickerRef.current.contains(e.target)) {
        setIsMoodPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMoodPickerOpen]);

  // Initialize local notes state only when modal opens for a new session id
  useEffect(() => {
    if (session && isOpen && session.id !== lastSavedSessionId) {
      const sessionNotes = session.proof_note || '';
      setNotes(sessionNotes);
      // Seed structured drafts from stored values (null-safe)
      setWinDraft(session.win_note || '');
      setNextMoveDraft(session.next_move || '');
      setStructuredSaveError(null);
      setSaveError(null);
      // Reset goal-edit state when switching sessions
      setIsEditingGoal(false);
      setGoalDraft('');
      setGoalSaveError(null);
      // Close mood picker when switching sessions
      setIsMoodPickerOpen(false);
      
      // Log modal open with first 20 characters for debugging
      const previewText = sessionNotes.substring(0, 20);
      console.log('NOTES_OPEN', { sessionId: session.id, preview: previewText });
      
      // Check for mismatch if we have previously saved notes for this session
      if (lastSavedSessionId === session.id && lastSavedNotes !== sessionNotes) {
        console.log('NOTES_MISMATCH', { 
          sessionId: session.id, 
          expected: lastSavedNotes.substring(0, 20),
          actual: previewText 
        });
        // Refresh session from storage - this would be handled by parent component
        // For now, we trust the session prop as the source of truth
      }
    }
  }, [session?.id, isOpen]); // Only reset when session ID changes or modal opens

  if (!session) return null;

  // Comprehensive save function with user scoping and persistence
  const saveNotes = async (sessionId, notesToSave) => {
    try {
      // Log save attempt
      console.log('NOTES_SAVE_CLICK', { 
        sessionId, 
        length: notesToSave.length 
      });

      // Write to backend with user scoping
      const response = await privateAPI.patch(`/sessions/${sessionId}/notes`, {
        notes: notesToSave // Preserve all formatting - no trim or transform
      });

      // Get updated session object (if backend returns it)
      let updatedSession = response.data?.session;
      
      // If no session object returned, merge notes into current session
      if (!updatedSession) {
        updatedSession = {
          ...session,
          proof_note: notesToSave
        };
      }

      // Update in-memory state immediately
      onUpdateSession(sessionId, {
        proof_note: notesToSave
      }).catch(() => {});

      // Confirm the save by re-reading the session
      try {
        const confirmResponse = await privateAPI.get(`/sessions/${sessionId}`);
        const confirmedNotes = confirmResponse.data?.proof_note || '';
        
        if (confirmedNotes !== notesToSave) {
          // Retry once if confirmation doesn't match
          console.warn('Notes confirmation mismatch, retrying save...');
          
          const retryResponse = await privateAPI.patch(`/sessions/${sessionId}/notes`, {
            notes: notesToSave
          });
          
          // Check again after retry
          const reconfirmResponse = await privateAPI.get(`/sessions/${sessionId}`);
          const reconfirmedNotes = reconfirmResponse.data?.proof_note || '';
          
          if (reconfirmedNotes !== notesToSave) {
            throw new Error('Notes save confirmation failed after retry');
          }
        }
      } catch (confirmError) {
        console.warn('Notes confirmation check failed, but proceeding:', confirmError);
      }

      // Track last saved state for mismatch detection
      setLastSavedSessionId(sessionId);
      setLastSavedNotes(notesToSave);

      // Log successful save
      console.log('NOTES_SAVE_OK', { sessionId });
      
      return updatedSession;
          } catch (error) {
      console.error('Failed to save notes:', error);
      throw error;
    }
  };

  const handleSaveNotes = async () => {
    setSaveError(null);
    setIsSaving(true);
    
    try {
      await saveNotes(session.id, notes);
      // Notes successfully saved - modal stays open with updated text visible
    } catch (error) {
      setSaveError('Failed to save notes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Save structured Win / Next Move using PATCH /sessions/{id}.
  // Also mirrors a deterministic compatibility proof_note for legacy consumers.
  const handleSaveStructured = async () => {
    setStructuredSaveError(null);
    setIsSavingStructured(true);
    const win = (winDraft || '').trim();
    const nm = (nextMoveDraft || '').trim();
    const mirrorLines = [];
    if (win) mirrorLines.push(`Win: ${win}`);
    if (nm) mirrorLines.push(`Next Move: ${nm}`);
    const compatNote = mirrorLines.join('\n');
    try {
      await privateAPI.patch(`/sessions/${session.id}`, {
        win_note: win || null,
        next_move: nm || null,
        proof_note: compatNote
      });
      onUpdateSession(session.id, {
        win_note: win || null,
        next_move: nm || null,
        proof_note: compatNote
      }).catch(() => {});
    } catch (error) {
      setStructuredSaveError('Failed to save. Please try again.');
    } finally {
      setIsSavingStructured(false);
    }
  };

  // Start editing goal title (modal-only)
  const handleStartGoalEdit = () => {
    setGoalDraft(session.goal || '');
    setGoalSaveError(null);
    setIsEditingGoal(true);
  };

  const handleCancelGoalEdit = () => {
    setIsEditingGoal(false);
    setGoalDraft('');
    setGoalSaveError(null);
  };

  const handleSaveGoal = async () => {
    const trimmed = (goalDraft || '').trim();
    if (!trimmed) {
      setGoalSaveError('Title cannot be empty.');
      return;
    }
    if (trimmed === (session.goal || '')) {
      setIsEditingGoal(false);
      return;
    }
    setIsSavingGoal(true);
    setGoalSaveError(null);
    try {
      await onUpdateSession(session.id, { goal: trimmed });
      setIsEditingGoal(false);
    } catch (error) {
      setGoalSaveError('Failed to save title. Please try again.');
    } finally {
      setIsSavingGoal(false);
    }
  };

  // Mark a subtask as completed (modal only) — no confirmation
  const handleCompleteSubtask = async (taskId) => {
    if (!taskId || completingTaskId) return;
    setCompletingTaskId(taskId);

    // Compute updated tasks locally so UI reflects change immediately
    const completedAt = new Date().toISOString();
    const updatedTasks = (session.tasks || []).map(t =>
      t && t.id === taskId
        ? { ...t, status: 'completed', completed_at: t.completed_at || completedAt }
        : t
    );

    try {
      await onUpdateSession(session.id, {
        complete_task_id: taskId,
        tasks: updatedTasks
      });
    } catch (error) {
      console.error('Failed to complete subtask:', error);
    } finally {
      setCompletingTaskId(null);
    }
  };

  // Undo a completed subtask (modal only) — no confirmation
  const handleUndoSubtask = async (taskId) => {
    if (!taskId || undoingTaskId) return;
    setUndoingTaskId(taskId);

    const updatedTasks = (session.tasks || []).map(t =>
      t && t.id === taskId
        ? { ...t, status: 'pending', completed_at: null }
        : t
    );

    try {
      await onUpdateSession(session.id, {
        undo_task_id: taskId,
        tasks: updatedTasks
      });
    } catch (error) {
      console.error('Failed to undo subtask:', error);
    } finally {
      setUndoingTaskId(null);
    }
  };

  // Toggle status between Completed and In Progress with confirmation
  const handleToggleStatus = async () => {
    // Use parent component's toggle handler which shows branded modal
    onToggleStatus(session.id, session.finished);
  };

  const formatDateTime = (dateString) => {
    return formatJournalDateTime(dateString);
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const isInProgress = !session.completed_at;
  const sessionTasks = session.tasks || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto" style={{ pointerEvents: 'auto' }}>
        <DialogHeader>
          <DialogTitle className="text-white text-xl font-semibold">
            Sprint Journal
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 pt-4">
          {/* Sprint Goal */}
          <div>
            {isEditingGoal ? (
              <div className="space-y-2 mb-2">
                <Input
                  data-testid="session-goal-edit-input"
                  value={goalDraft}
                  onChange={(e) => setGoalDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveGoal();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancelGoalEdit();
                    }
                  }}
                  placeholder="Sprint goal / title"
                  className="bg-slate-700 border-slate-600 text-white text-base sm:text-lg"
                  autoFocus
                />
                {goalSaveError && (
                  <div className="text-red-400 text-xs">{goalSaveError}</div>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    size="sm"
                    data-testid="session-goal-save-button"
                    onClick={handleSaveGoal}
                    disabled={isSavingGoal || !goalDraft.trim()}
                    className="bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
                  >
                    {isSavingGoal ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    data-testid="session-goal-cancel-button"
                    onClick={handleCancelGoalEdit}
                    disabled={isSavingGoal}
                    className="text-slate-300 hover:text-white"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 mb-2">
                <h2 className="text-lg font-medium text-white break-words flex-1">{session.goal}</h2>
                <button
                  type="button"
                  data-testid="session-goal-edit-button"
                  onClick={(e) => { e.stopPropagation(); handleStartGoalEdit(); }}
                  className="flex-shrink-0 mt-0.5 p-1.5 rounded text-slate-400 hover:text-amber-400 hover:bg-slate-700/60 transition-colors duration-150"
                  aria-label="Edit sprint title"
                  title="Edit title"
                >
                  <Edit className="h-4 w-4" />
                </button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
              <span>{formatDateTime(session.session_date)}</span>
              <span>•</span>
              <span>{formatDuration(session.duration_minutes)}</span>
              <span>•</span>
              <Badge 
                className={`cursor-pointer ${session.finished ? "bg-green-600 hover:bg-green-700" : "bg-orange-600 hover:bg-orange-700"}`}
                onClick={handleToggleStatus}
              >
                {session.finished ? "Completed" : "In Progress"}
              </Badge>
              {session.mood_rating && (
                <div className="relative">
                  <button
                    type="button"
                    data-testid={`session-modal-mood-emoji-${session.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMoodPickerOpen(prev => !prev);
                    }}
                    className="text-base sm:text-lg p-1 rounded hover:bg-slate-700/60 active:bg-slate-600 transition-colors duration-150 leading-none"
                    aria-label="Change mood for this sprint"
                    title="Change mood"
                  >
                    {['😫', '😔', '😐', '😊', '😆'][session.mood_rating - 1]}
                  </button>
                  {isMoodPickerOpen && (
                    <div
                      ref={moodPickerRef}
                      data-testid={`session-modal-mood-picker-${session.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute right-0 top-full mt-1 z-50 flex items-center gap-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl px-2 py-1.5 max-w-[calc(100vw-2rem)]"
                    >
                      {['😫', '😔', '😐', '😊', '😆'].map((emoji, index) => {
                        const newRating = index + 1;
                        const isSelected = session.mood_rating === newRating;
                        return (
                          <button
                            key={emoji}
                            type="button"
                            data-testid={`session-modal-mood-option-${session.id}-${newRating}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsMoodPickerOpen(false);
                              if (!isSelected) {
                                onUpdateSession(session.id, { mood_rating: newRating }).catch(() => {});
                              }
                            }}
                            className={`text-lg leading-none rounded p-1 transition-colors duration-150 hover:bg-slate-700 active:bg-slate-600 ${isSelected ? 'bg-slate-700 ring-1 ring-amber-500/60' : ''}`}
                            aria-label={`Set mood to ${emoji}`}
                          >
                            {emoji}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sub-tasks */}
          {sessionTasks.length > 0 && (
            <div>
              <h3 className="text-white font-medium mb-3 flex items-center space-x-2">
                <CheckCircle2 className="h-4 w-4" />
                <span>Tasks ({sessionTasks.filter(t => t.status === 'completed').length}/{sessionTasks.length})</span>
              </h3>
              <div className="space-y-2">
                {sessionTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`group flex items-center space-x-3 p-2 rounded-lg ${
                      task.status === 'completed' ? 'bg-green-900/20' : 'bg-slate-700/30'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {task.status === 'completed' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                      ) : (
                        <Circle className="h-4 w-4 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-grow min-w-0">
                      <div className={`text-sm ${
                        task.status === 'completed' ? 'text-green-400' : 'text-white'
                      }`}>
                        {task.title}
                      </div>
                      {task.estimated_minutes && (
                        <div className="text-xs text-slate-500">
                          {task.estimated_minutes} minutes
                        </div>
                      )}
                    </div>
                    {task.status !== 'completed' ? (
                      <button
                        type="button"
                        data-testid={`session-task-complete-${task.id}`}
                        disabled={completingTaskId === task.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCompleteSubtask(task.id);
                        }}
                        className="flex-shrink-0 ml-2 px-2.5 py-1 text-xs font-medium rounded bg-green-600 hover:bg-green-700 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 focus:opacity-100 focus-within:opacity-100 transition-opacity duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                        aria-label={`Mark task ${task.title} complete`}
                      >
                        {completingTaskId === task.id ? '...' : 'Complete'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        data-testid={`session-task-undo-${task.id}`}
                        disabled={undoingTaskId === task.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUndoSubtask(task.id);
                        }}
                        className="flex-shrink-0 ml-2 px-2.5 py-1 text-xs font-medium rounded bg-slate-600 hover:bg-slate-500 text-slate-100 border border-slate-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 focus:opacity-100 focus-within:opacity-100 transition-opacity duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                        aria-label={`Undo completion of task ${task.title}`}
                      >
                        {undoingTaskId === task.id ? '...' : 'Undo'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes Section */}
          <div>
            <h3 className="text-white font-medium mb-3 flex items-center space-x-2">
              <Edit className="h-4 w-4" />
              <span>Notes</span>
            </h3>
            {isInProgress ? (
              <div className="p-4 bg-amber-900/20 border border-amber-700/30 rounded-lg">
                <p className="text-amber-400 text-sm">
                  Notes can be edited after the sprint is completed.
                </p>
              </div>
            ) : (session.win_note != null || session.next_move != null) ? (
              // Structured session — edit Win and Next Move separately
              <div className="space-y-3" style={{ pointerEvents: 'auto' }}>
                <div>
                  <label className="text-slate-300 text-xs uppercase tracking-wide mb-1 block">
                    Win
                  </label>
                  <Textarea
                    data-testid="journal-win-input"
                    value={winDraft}
                    onChange={(e) => setWinDraft(e.target.value)}
                    placeholder="What did you complete?"
                    className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 min-h-[64px] resize-none"
                    style={{ pointerEvents: 'auto' }}
                  />
                </div>
                <div>
                  <label className="text-slate-300 text-xs uppercase tracking-wide mb-1 block">
                    Next Move
                  </label>
                  <Textarea
                    data-testid="journal-next-move-input"
                    value={nextMoveDraft}
                    onChange={(e) => setNextMoveDraft(e.target.value)}
                    placeholder="What will you do first for your next Sprint?"
                    className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 min-h-[64px] resize-none"
                    style={{ pointerEvents: 'auto' }}
                  />
                </div>
                {structuredSaveError && (
                  <div className="text-red-400 text-sm bg-red-900/20 border border-red-700/30 rounded px-3 py-2">
                    {structuredSaveError}
                  </div>
                )}
                <Button
                  type="button"
                  onClick={handleSaveStructured}
                  disabled={
                    isSavingStructured ||
                    ((winDraft || '') === (session.win_note || '') &&
                      (nextMoveDraft || '') === (session.next_move || ''))
                  }
                  size="sm"
                  data-testid="journal-save-structured-button"
                  className="bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 relative z-10"
                  style={{ pointerEvents: 'auto' }}
                >
                  {isSavingStructured ? 'Saving...' : 'Save'}
                </Button>
              </div>
            ) : (
              // Legacy session — preserve the existing combined Notes editor
              <div className="space-y-3" style={{ pointerEvents: 'auto' }}>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="No notes recorded for this sprint."
                  className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 min-h-[100px] resize-none"
                  style={{ pointerEvents: 'auto' }}
                />
                
                {/* Save error display */}
                {saveError && (
                  <div className="text-red-400 text-sm bg-red-900/20 border border-red-700/30 rounded px-3 py-2">
                    {saveError}
                  </div>
                )}
                
                <Button
                  type="button"
                  onClick={handleSaveNotes}
                  disabled={isSaving || notes === (session.proof_note || '')}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 relative z-10"
                  style={{ pointerEvents: 'auto' }}
                >
                  {isSaving ? 'Saving...' : 'Save Notes'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Focus Mode Dialog Component
const FocusModeDialog = ({ onStart, disabled = false }) => {
  const [open, setOpen] = useState(false);

  const handleStart = () => {
    setOpen(false);
    onStart();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="bg-slate-700/50 border-slate-600 text-white hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={disabled}
        >
          <Smartphone className="h-4 w-4 mr-2" />
          Focus Mode
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white">Get in the zone</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-slate-300">
            Enter a clean, distraction-free sprint view.
          </p>
          <div className="flex space-x-4">
            <Button 
              onClick={handleStart}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              Start
            </Button>
            <Button 
              variant="ghost" 
              className="text-white" 
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Login Component
const LoginScreen = ({ onLogin }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    setIsLoading(true);
    const success = await onLogin();
    if (!success) {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <LogoTile 
            size="h-20 w-20" 
            cornerRadius="rounded-2xl"
            className="mx-auto mb-6"
          />
          <h1 className="text-4xl font-bold text-white mb-2">TimeTactix Sprint</h1>
          <p className="text-slate-400 text-lg">The 5-9 Focus Coach</p>
        </div>

        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-white">Welcome Back</h2>
              <p className="text-slate-400 mt-2">Continue your productivity journey</p>
            </div>
            
            <Button 
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium h-12"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Signing In...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <LogIn className="h-4 w-4" />
                  <span>Launch TimeTactix Sprint</span>
                </div>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Sprint Setup Component
const SprintSetup = ({ onStartSprint, onViewProgress, pendingCompletion, onCompleteLastSprint }) => {
  const [goal, setGoal] = useState('');
  const [duration, setDuration] = useState(30); // Default to 30 minutes
  const [isCreating, setIsCreating] = useState(false);
  const [breakSettings, setBreakSettings] = useState({
    30: { enabled: true, breakTimes: [], breakDuration: 5 }, // No breaks recommended
    60: { enabled: true, breakTimes: [45], breakDuration: 5 }, // Break at 45 min
    90: { enabled: false, breakTimes: [], breakDuration: 10 }, // Recovery after session
    120: { enabled: true, breakTimes: [60, 90], breakDuration: 10 } // Breaks at 60 and 90 min
  });
  // Lightweight sessions snapshot for the main-screen metric strip + Continue card
  const [sessionsSnapshot, setSessionsSnapshot] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await privateAPI.get('/sessions?limit=5000&skip=0');
        if (!cancelled) {
          setSessionsSnapshot(Array.isArray(res.data) ? res.data : res.data?.sessions || []);
        }
      } catch (_) {
        if (!cancelled) setSessionsSnapshot([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const setupMetrics = useMemo(() => computeSprintMetrics(sessionsSnapshot), [sessionsSnapshot]);
  const continueContext = useMemo(() => getLatestSessionWithNextMove(sessionsSnapshot), [sessionsSnapshot]);

  const handlePlanWithTactixAI = () => {
    if (!continueContext) return;
    const lastGoal = (continueContext.session.goal || 'Last Sprint').trim();
    const nextMove = continueContext.nextMove.trim();
    const message =
      `I just finished: “${lastGoal}”\n` +
      `My Next Move was: “${nextMove}”\n\n` +
      `Help me plan my next Sprint.`;
    window.dispatchEvent(
      new CustomEvent('tactixai:prefill-message', { detail: { message } })
    );
  };

  // TactixAI handoff: populate goal + duration + break toggle from a parsed Sprint Card
  useEffect(() => {
    const onApplySprintCard = (e) => {
      const card = e?.detail;
      if (!card || typeof card !== 'object') return;
      const allowed = [30, 60, 90, 120];
      if (!allowed.includes(card.duration)) return;

      if (typeof card.goal === 'string' && card.goal.trim()) {
        setGoal(card.goal.trim());
      }
      setDuration(card.duration);

      // Only apply breaks for 60/120 (30/90 have no break UI per existing app rules)
      if ((card.duration === 60 || card.duration === 120) && (card.breaks === 'ON' || card.breaks === 'OFF')) {
        setBreakSettings((prev) => ({
          ...prev,
          [card.duration]: {
            ...prev[card.duration],
            enabled: card.breaks === 'ON'
          }
        }));
      }
    };
    window.addEventListener('tactixai:apply-sprint-card', onApplySprintCard);
    return () => window.removeEventListener('tactixai:apply-sprint-card', onApplySprintCard);
  }, []);

  const durations = [
    { 
      value: 30, 
      label: '30 min', 
      name: 'Quick Start Sprint',
      description: 'Recommended: No break. Best for overcoming procrastination and making quick progress.'
    },
    { 
      value: 60, 
      label: '60 min', 
      name: 'Focused Work Sprint',
      description: 'Recommended: 1 × 5‑min break at the 45‑minute mark. Ideal for deep focus with a short reset.'
    },
    { 
      value: 90, 
      label: '90 min', 
      name: 'Standard Productivity Session',
      description: 'Recommended: 10‑min recovery after the session. Based on natural focus cycles — work hard, then recharge.'
    },
    { 
      value: 120, 
      label: '120 min', 
      name: 'Deep Work Session',
      description: 'Recommended: 2 × 10‑min breaks at 60 and 90 minutes. Designed for extended deep work with structured recovery.'
    }
  ];

  const toggleBreaks = (durationValue) => {
    setBreakSettings(prev => ({
      ...prev,
      [durationValue]: {
        ...prev[durationValue],
        enabled: !prev[durationValue].enabled
      }
    }));
  };

  const getCurrentBreakConfig = () => {
    const currentSettings = breakSettings[duration];
    if (!currentSettings.enabled || currentSettings.breakTimes.length === 0) {
      return null;
    }
    
    return {
      enabled: currentSettings.enabled,
      break_times: currentSettings.breakTimes,
      break_duration: currentSettings.breakDuration
    };
  };

  const handleCreateSprint = async () => {
    if (!goal.trim()) return;
    
    setIsCreating(true);
    try {
      const breakConfig = getCurrentBreakConfig();
      
      const requestData = {
        goal: goal.trim(),
        duration_minutes: duration
      };
      
      if (breakConfig) {
        requestData.break_config = breakConfig;
      }
      
      const response = await privateAPI.post('/sprints', requestData);
      
      onStartSprint(response.data);
    } catch (error) {
      console.error('Failed to create sprint:', error);
      setIsCreating(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-8">
      {/* Navigation Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-2">
          <span className="text-xl font-semibold text-white">TimeTactix Sprint</span>
        </div>
        <div className="flex items-center space-x-4">
          {pendingCompletion && (
            <Button 
              onClick={onCompleteLastSprint}
              variant="outline" 
              size="sm"
              className="bg-orange-600/20 border-orange-500/30 text-orange-300 hover:text-white hover:bg-orange-600"
            >
              <FileCheck className="h-4 w-4 mr-2" />
              Complete Last Sprint
            </Button>
          )}
          <Button 
            onClick={onViewProgress} 
            variant="ghost" 
            size="sm"
            className="text-slate-300 hover:text-white"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Sprint Journal
          </Button>
        </div>
      </div>
      
      <div className="text-center space-y-4">
        <LogoTile 
          size="h-16 w-16" 
          cornerRadius="rounded-xl"
          className="mx-auto"
        />
        <h1 className="text-3xl font-bold text-white">Start Your Sprint</h1>
        <p className="text-slate-400">What do you want to accomplish?</p>
      </div>

      {/* Compact main-screen metric strip: Daily / Weekly / Sessions This Week */}
      <div
        data-testid="setup-metric-strip"
        className="grid grid-cols-3 gap-2 sm:gap-3"
      >
        {[
          { label: 'Daily Streak', value: setupMetrics.current_streak, suffix: setupMetrics.current_streak === 1 ? 'day' : 'days' },
          { label: 'Weekly Streak', value: setupMetrics.weekly_streak, suffix: setupMetrics.weekly_streak === 1 ? 'week' : 'weeks' },
          { label: 'Sessions This Week', value: setupMetrics.sessions_this_week, suffix: null },
        ].map((m) => (
          <div
            key={m.label}
            data-testid={`setup-metric-${m.label.toLowerCase().replace(/\s+/g,'-')}`}
            className="rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2.5 flex flex-col items-start"
          >
            <div className="text-[10px] uppercase tracking-wide text-slate-400 leading-tight">
              {m.label}
            </div>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="text-xl sm:text-2xl font-semibold text-amber-400 leading-none">
                {m.value}
              </span>
              {m.suffix && (
                <span className="text-[11px] text-slate-500">{m.suffix}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Continue where you left off — only when an explicit Next Move exists */}
      {continueContext && (
        <div
          data-testid="continue-card"
          className="rounded-lg border border-slate-700 bg-slate-800/50 px-4 py-3 space-y-2"
        >
          <div className="text-xs text-amber-300/90 font-medium">
            Continue where you left off
          </div>
          <div className="space-y-1.5">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">
                Last Sprint
              </div>
              <div
                className="text-sm text-slate-200 leading-snug break-words line-clamp-2"
                data-testid="continue-last-sprint"
              >
                {continueContext.session.goal || 'Last Sprint'}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">
                Next Move
              </div>
              <div
                className="text-sm text-white leading-snug break-words"
                data-testid="continue-next-move"
              >
                {continueContext.nextMove}
              </div>
            </div>
          </div>
          <div className="pt-1">
            <Button
              type="button"
              onClick={handlePlanWithTactixAI}
              data-testid="continue-plan-with-tactixai"
              size="sm"
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
            >
              Plan this with TactixAI
            </Button>
          </div>
        </div>
      )}

      <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-white">Sprint Goal</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Textarea
            placeholder="E.g. &apos;Outline tomorrow&apos;s content plan.&apos;&#10;Stuck? Ask TactixAI and paste your goal here."
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 min-h-[100px]"
          />

          <div>
            <h3 className="text-white font-medium mb-2">Choose Duration</h3>
            <p className="text-slate-400 text-sm mb-4">TactixAI can recommend a Sprint length if you&apos;re unsure.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {durations.map((option) => {
                const currentBreakSetting = breakSettings[option.value];
                const hasBreaks = currentBreakSetting.breakTimes.length > 0;
                
                return (
                  <Card
                    key={option.value}
                    className={`cursor-pointer transition-all ${
                      duration === option.value
                        ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500'
                        : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                    }`}
                    onClick={() => setDuration(option.value)}
                  >
                    <CardContent className="p-4">
                      <div className="text-center mb-3">
                        <div className="text-2xl font-bold text-white mb-1">{option.label}</div>
                        <div className="text-lg font-medium text-slate-200 mb-2">{option.name}</div>
                        <div className="text-sm text-slate-400 leading-relaxed">{option.description}</div>
                      </div>
                      
                      {/* Break Toggle Section */}
                      {hasBreaks && (
                        <div className="border-t border-slate-600 pt-3 mt-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-300">
                              Breaks {currentBreakSetting.enabled ? 'ON' : 'OFF'}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleBreaks(option.value);
                              }}
                              className={`w-10 h-5 rounded-full transition-colors duration-200 relative ${
                                currentBreakSetting.enabled 
                                  ? 'bg-amber-500' 
                                  : 'bg-slate-500'
                              }`}
                            >
                              <div
                                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-200 ${
                                  currentBreakSetting.enabled 
                                    ? 'translate-x-5' 
                                    : 'translate-x-0.5'
                                }`}
                              />
                            </button>
                          </div>
                          {currentBreakSetting.enabled && (
                            <div className="text-xs text-slate-400 mt-1">
                              {currentBreakSetting.breakTimes.length === 1 
                                ? `Break at ${currentBreakSetting.breakTimes[0]} min` 
                                : `Breaks at ${currentBreakSetting.breakTimes.join(' and ')} min`
                              }
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          <Button
            onClick={handleCreateSprint}
            disabled={!goal.trim() || isCreating}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium h-12"
          >
            {isCreating ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Creating Sprint...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Play className="h-4 w-4" />
                <span>Start Sprint</span>
              </div>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// BreakUIController - Root-level controller for break modals across all views
const BreakUIController = ({ timer, sprint, activeSprint, focusMode }) => {
  // Break system state - mutually exclusive flags
  const [showBreakAlert, setShowBreakAlert] = useState(false);
  const [showBreakEndAlert, setShowBreakEndAlert] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakTimeLeft, setBreakTimeLeft] = useState(0);
  const [currentBreakOffset, setCurrentBreakOffset] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  
  // Completion modal state
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const completionHandledForSprint = useRef(null); // Track which sprint we've shown completion for
  const completionAudioRef = useRef(null);
  const lastPlayedAudioForSprint = useRef(null); // Track which sprint has played audio (idempotency)
  
  // Break system refs for hardened state management
  const breakEndTimeoutRef = useRef(null);
  const breakIntervalRef = useRef(null);
  const lastHandledOffsetMs = useRef(null);
  const suppressBreakUIUntil = useRef(0);
  const currentSprintIdRef = useRef(null); // Track current sprint ID for reset

  // Audio system with format detection and user interaction-based loading
  const audioLoadedRef = useRef(false);
  const audioContextRef = useRef(null); // For Web Audio API fallback
  
  useEffect(() => {
    // Create audio element on mount
    if (!completionAudioRef.current) {
      const audio = new Audio();
      audio.preload = 'auto';
      audio.volume = 0.5; // Subtle volume
      completionAudioRef.current = audio;
    }
    
    // Set up audio source on first user interaction
    const loadAudioOnInteraction = () => {
      if (audioLoadedRef.current) return;
      
      // Load completion audio
      const completionAudio = completionAudioRef.current;
      if (completionAudio) {
        let audioSrc = null;
        if (completionAudio.canPlayType && completionAudio.canPlayType('audio/mpeg').replace(/no/, '')) {
          audioSrc = '/sounds/completion-ping.mp3';
        } else if (completionAudio.canPlayType && completionAudio.canPlayType('audio/ogg').replace(/no/, '')) {
          audioSrc = '/sounds/completion-ping.ogg';
        }
        
        if (audioSrc) {
          completionAudio.src = audioSrc;
          completionAudio.addEventListener('error', () => {
            console.log('[COMPLETE_AUDIO] File not found, using generated beep');
          }, { once: true });
          completionAudio.addEventListener('canplaythrough', () => {
            console.log('[COMPLETE_AUDIO] Loaded source:', audioSrc);
          }, { once: true });
          completionAudio.load();
        } else {
          console.log('[COMPLETE_AUDIO] no supported source, will use generated beep');
        }
      }
      
      // Initialize Web Audio API context for fallback
      if (!audioContextRef.current && (window.AudioContext || window.webkitAudioContext)) {
        try {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        } catch (e) {
          console.log('[AUDIO] Web Audio API not available');
        }
      }
      
      audioLoadedRef.current = true;
      
      // Remove listeners after first load
      document.removeEventListener('click', loadAudioOnInteraction);
      document.removeEventListener('keydown', loadAudioOnInteraction);
    };
    
    // Listen for first user interaction
    document.addEventListener('click', loadAudioOnInteraction);
    document.addEventListener('keydown', loadAudioOnInteraction);
    
    return () => {
      document.removeEventListener('click', loadAudioOnInteraction);
      document.removeEventListener('keydown', loadAudioOnInteraction);
    };
  }, []);

  // Reset break controller state on new sprint start
  useEffect(() => {
    const sprintId = activeSprint?.id;
    
    // If sprint ID changed, reset all break state completely
    if (sprintId && currentSprintIdRef.current !== sprintId) {
      console.log('BREAK_UI_CONTROLLER_RESET', {
        previousSprintId: currentSprintIdRef.current,
        newSprintId: sprintId
      });
      
      // Clear all modal states (including completion)
      setShowBreakAlert(false);
      setShowBreakEndAlert(false);
      setIsOnBreak(false);
      setBreakTimeLeft(0);
      setCurrentBreakOffset(null);
      setIsClosing(false);
      setShowCompletionModal(false);
      completionHandledForSprint.current = null;
      lastPlayedAudioForSprint.current = null; // Reset audio guard for new sprint
      
      // Clear all refs and timers
      lastHandledOffsetMs.current = null;
      suppressBreakUIUntil.current = 0;
      
      if (breakEndTimeoutRef.current) {
        clearTimeout(breakEndTimeoutRef.current);
        breakEndTimeoutRef.current = null;
      }
      
      if (breakIntervalRef.current) {
        clearInterval(breakIntervalRef.current);
        breakIntervalRef.current = null;
      }
      
      // Update current sprint ID reference
      currentSprintIdRef.current = sprintId;
    }
  }, [activeSprint?.id]);

  // Root-level event listener for break triggers - always mounted
  useEffect(() => {
    const handleBreakStart = (event) => {
      const { sprintId, offsetMs, testMode } = event.detail;
      const inFocusMode = focusMode || false;
      console.log(`Break UI Event Received (root) with {inFocusMode: ${inFocusMode}}`);
      
      // Ensure we're dealing with the current sprint
      if (sprintId !== activeSprint?.id) {
        console.log(`Break UI ignored - sprint mismatch: event=${sprintId}, current=${activeSprint?.id}`);
        return;
      }
      
      // Check suppression to avoid race conditions (but don't suppress view switches)
      if (Date.now() < suppressBreakUIUntil.current) {
        console.log(`Break UI suppressed until ${suppressBreakUIUntil.current}, ignoring`);
        return;
      }
      
      // For new sprint, reset lastHandledOffsetMs to allow breaks
      if (currentSprintIdRef.current === sprintId && lastHandledOffsetMs.current === null) {
        // Fresh sprint, allow all breaks
      }
      
      // Guard against duplicate opens for same offset in same sprint
      if (showBreakAlert || isOnBreak || (lastHandledOffsetMs.current === offsetMs && currentSprintIdRef.current === sprintId)) {
        console.log(`Break UI already open or duplicate offset ${offsetMs} for sprint ${sprintId}, ignoring`);
        return;
      }
      
      // Set last handled offset to prevent duplicates for this sprint
      lastHandledOffsetMs.current = offsetMs;
      
      // Open break modal - single source of truth (shows "Take a break?" prompt)
      setCurrentBreakOffset(offsetMs);
      setShowBreakAlert(true);
      setIsOnBreak(false);
      setShowBreakEndAlert(false);
      
      console.log("Break UI Opened (root)");
      
      // Safety check on next tick - ensure modal opens
      setTimeout(() => {
        if (!showBreakAlert && !isOnBreak && currentSprintIdRef.current === sprintId) {
          console.log("Break UI safety retry - modal not visible, opening again");
          setShowBreakAlert(true);
          setCurrentBreakOffset(offsetMs);
        }
      }, 50);
    };
    
    // Single root event listener for app lifetime
    window.addEventListener('ttx:break-start', handleBreakStart);
    
    return () => {
      window.removeEventListener('ttx:break-start', handleBreakStart);
    };
  }, [focusMode, showBreakAlert, isOnBreak, activeSprint?.id]); // Include activeSprint?.id for sprint change detection

  // Root-level event listener for sprint completion - always mounted
  useEffect(() => {
    const handleSprintComplete = (event) => {
      const { sprintId } = event.detail || {};
      const inFocusMode = focusMode || false;
      console.log(`Completion Event Received (root) with {inFocusMode: ${inFocusMode}, sprintId: ${sprintId}}`);
      
      // Ensure we're dealing with the current sprint
      if (sprintId && sprintId !== activeSprint?.id) {
        console.log(`Completion ignored - sprint mismatch: event=${sprintId}, current=${activeSprint?.id}`);
        return;
      }
      
      // Guard against duplicate opens for same sprint
      if (completionHandledForSprint.current === sprintId) {
        console.log(`Completion already shown for sprint ${sprintId}, ignoring`);
        return;
      }
      
      // PRIORITY RULE: Close any break modals when sprint completes
      if (showBreakAlert || isOnBreak || showBreakEndAlert) {
        console.log('COMPLETION_PRIORITY: Closing break modal to show completion');
        setShowBreakAlert(false);
        setIsOnBreak(false);
        setShowBreakEndAlert(false);
        setCurrentBreakOffset(null);
        
        // Clear break timers
        if (breakIntervalRef.current) {
          clearInterval(breakIntervalRef.current);
          breakIntervalRef.current = null;
        }
        if (breakEndTimeoutRef.current) {
          clearTimeout(breakEndTimeoutRef.current);
          breakEndTimeoutRef.current = null;
        }
      }
      
      // Mark this sprint as handled
      completionHandledForSprint.current = sprintId;
      
      // Show completion modal
      setShowCompletionModal(true);
      
      // Play completion sound - DOUBLE DING (idempotent per sprintId)
      const playCompletionSoundOnceFor = (targetSprintId) => {
        // Idempotency guard: only play once per sprint
        if (lastPlayedAudioForSprint.current === targetSprintId) {
          console.log('[COMPLETE_AUDIO] Already played for sprint', targetSprintId);
          return;
        }
        
        // Mark as played for this sprint
        lastPlayedAudioForSprint.current = targetSprintId;
        
        const audio = completionAudioRef.current;
        const COMPLETE_DING_GAP_MS = 750;
        
        // Try to play audio file if it's loaded (DOUBLE DING)
        if (audio && audio.src && audio.readyState >= 2) {
          console.log('[COMPLETE_AUDIO] using FILE');
          
          try {
            // Pre-prime second audio element for better reliability
            const second = new Audio(audio.src);
            second.volume = audio.volume;
            second.load();
            
            // First ding - use original audio element
            audio.play().catch((error) => {
              console.log('[COMPLETE_AUDIO] First ding blocked:', error.message);
            });
            
            // Second ding after 2000ms - use pre-primed audio element
            setTimeout(() => {
              try {
                second.play().catch((error) => {
                  console.log('[COMPLETE_AUDIO] Second ding blocked:', error.message);
                });
              } catch (error) {
                // Silent fail on second ding
              }
            }, COMPLETE_DING_GAP_MS);
            
            return;
          } catch (error) {
            console.log('[COMPLETE_AUDIO] Error playing file:', error.message);
          }
        }
        
        // Fallback: Generate double beep using Web Audio API
        if (audioContextRef.current) {
          console.log('[COMPLETE_AUDIO] using FALLBACK');
          
          try {
            const ctx = audioContextRef.current;
            
            // Helper function to create single beep
            const createBeep = (startTime) => {
              const oscillator = ctx.createOscillator();
              const gainNode = ctx.createGain();
              
              oscillator.connect(gainNode);
              gainNode.connect(ctx.destination);
              
              oscillator.frequency.value = 800; // 800 Hz pleasant tone
              oscillator.type = 'sine';
              
              gainNode.gain.setValueAtTime(0.3, startTime); // Start volume
              gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2); // Fade out
              
              oscillator.start(startTime);
              oscillator.stop(startTime + 0.2); // 200ms duration
            };
            
            // First beep
            createBeep(ctx.currentTime);
            
            // Second beep after 2000ms (same gap as file playback)
            createBeep(ctx.currentTime + (COMPLETE_DING_GAP_MS / 1000));
            
            console.log('[COMPLETE_AUDIO] Double beep generated');
          } catch (error) {
            console.log('[COMPLETE_AUDIO] Web Audio API error:', error.message);
          }
        }
      };
      
      // Play double ding for this sprint (idempotent)
      playCompletionSoundOnceFor(sprintId);
      
      console.log("Completion Modal Opened (root)");
    };
    
    // Single root event listener for app lifetime
    window.addEventListener('ttx:sprint-complete', handleSprintComplete);
    
    return () => {
      window.removeEventListener('ttx:sprint-complete', handleSprintComplete);
    };
  }, [focusMode, showBreakAlert, isOnBreak, showBreakEndAlert, activeSprint?.id]);

  // Break control functions
  const skipBreak = (source = 'button') => {
    // Debounce to prevent double-invocation
    if (isClosing) return;
    setIsClosing(true);
    
    const logMessage = source === 'closeIcon' ? "Break UI Closed (close icon)" : "Break UI Closed";
    console.log(logMessage);
    
    // No chime on manual close/skip (silent)
    
    // Clear all break modal states - mutually exclusive enforcement
    setShowBreakAlert(false);
    setIsOnBreak(false);
    setShowBreakEndAlert(false);
    setCurrentBreakOffset(null);
    
    // Clear any active break intervals and timeouts
    if (breakIntervalRef.current) {
      clearInterval(breakIntervalRef.current);
      breakIntervalRef.current = null;
    }
    if (breakEndTimeoutRef.current) {
      clearTimeout(breakEndTimeoutRef.current);
      breakEndTimeoutRef.current = null;
    }
    
    // Set suppression to prevent race conditions
    suppressBreakUIUntil.current = Date.now() + 1500;
    
    // Use existing resume path to unpause timer
    timer?.resume();
    console.log("Break Resumed with {isPaused: false, isRunning: true}");
    
    // Reset debounce flag after a short delay
    setTimeout(() => setIsClosing(false), 300);
  };

  const startBreak = () => {
    // Debounce to prevent double-invocation
    if (isClosing) return;
    setIsClosing(true);
    
    console.log("Break Timer Started");
    
    // Clear all break modal states - mutually exclusive enforcement
    setShowBreakAlert(false);
    setIsOnBreak(true);
    setShowBreakEndAlert(false);
    
    const breakDuration = sprint?.break_config?.break_duration || 5;
    setBreakTimeLeft(breakDuration * 60); // Convert to seconds
    
    // Set suppression to prevent race conditions
    suppressBreakUIUntil.current = Date.now() + 1500;
    
    // Start break countdown (timer remains paused during break)
    breakIntervalRef.current = setInterval(() => {
      setBreakTimeLeft((prev) => {
        if (prev <= 1) {
          // Break is over - clear interval first
          if (breakIntervalRef.current) {
            clearInterval(breakIntervalRef.current);
            breakIntervalRef.current = null;
          }
          
          // Set end alert state
          setIsOnBreak(false);
          setShowBreakEndAlert(true);
          
          // Auto-close break end alert after 8 seconds
          breakEndTimeoutRef.current = setTimeout(() => {
            closeBreakEndAlert();
          }, 8000);
          
          // Auto-resume work session using existing resume path
          timer?.resume();
          console.log("Break Resumed with {isPaused: false, isRunning: true}");
          
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Reset debounce flag after a short delay
    setTimeout(() => setIsClosing(false), 300);
  };

  const closeBreakEndAlert = () => {
    console.log("Break End Alert Closed");
    
    // Clear all break modal states - mutually exclusive enforcement  
    setShowBreakAlert(false);
    setIsOnBreak(false);
    setShowBreakEndAlert(false);
    setCurrentBreakOffset(null);
    
    // Clear any active timeouts
    if (breakEndTimeoutRef.current) {
      clearTimeout(breakEndTimeoutRef.current);
      breakEndTimeoutRef.current = null;
    }
    
    // Set suppression to prevent race conditions
    suppressBreakUIUntil.current = Date.now() + 1500;
  };

  // Completion control function
  const dismissCompletion = () => {
    console.log("Completion Modal Dismissed");
    
    // Close completion modal
    setShowCompletionModal(false);
    
    // No timer manipulation - leave UI at 0:00 as requested
  };

  // Format time for break display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Body scroll lock when modals are open
  useEffect(() => {
    if (showBreakAlert || isOnBreak || showCompletionModal) {
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '0px'; // Prevent layout shift
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [showBreakAlert, isOnBreak, showCompletionModal]);

  // Cleanup break timers on unmount
  useEffect(() => {
    return () => {
      if (breakEndTimeoutRef.current) {
        clearTimeout(breakEndTimeoutRef.current);
      }
      if (breakIntervalRef.current) {
        clearInterval(breakIntervalRef.current);
      }
    };
  }, []);

  // Don't render modal if no sprint context
  if (!sprint || !activeSprint) {
    return null;
  }

  // Render unified break modal via portal to document.body
  return createPortal(
    <>
      {/* Single Break Modal - mutually exclusive states - rendered above Focus Mode */}
      {(showBreakAlert || isOnBreak || showBreakEndAlert) && (
        <div className="fixed inset-0 flex items-center justify-center pointer-events-auto z-[99999]">
          {/* Dim overlay - above Focus Mode */}
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99998]" />
          
          {/* Centered modal panel - above Focus Mode and overlay */}
          <div className="relative bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-w-md w-full mx-4 z-[99999]">
            {(() => {
              // Log when modal is centered and mounted
              console.log("Break UI Centered OK");
              return null;
            })()}
            
            {/* Single Close Button - only shown for Break Alert and Break Timer */}
            {(showBreakAlert || isOnBreak) && (
              <button
                onClick={() => skipBreak('closeIcon')}
                aria-label="Close break modal"
                className="absolute top-4 right-4 w-6 h-6 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors z-[100000]"
                style={{ pointerEvents: 'auto' }}
                disabled={isClosing}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            
            {/* Break Alert Content */}
            {showBreakAlert && (
              <>
                <div className="p-6 pb-0">
                  <h2 className="text-white text-center text-xl font-semibold pr-8">
                    Time for a break! ☕
                  </h2>
                </div>
                <div className="p-6 space-y-6">
                  <div className="text-center">
                    <p className="text-slate-300 mb-4">
                      You've been focused for a while. Take a {sprint.break_config?.break_duration || 5}-minute break to recharge.
                    </p>
                    <p className="text-amber-400 font-medium">
                      Choose an option to continue
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      onClick={startBreak}
                      className="bg-green-600 hover:bg-green-700 text-white flex-1"
                      autoFocus
                      disabled={isClosing}
                    >
                      Start Break
                    </Button>
                    <Button 
                      onClick={() => skipBreak('button')}
                      variant="outline"
                      className="bg-slate-700/50 border-slate-600 text-white hover:bg-slate-600 flex-1"
                      disabled={isClosing}
                    >
                      Skip Break
                    </Button>
                  </div>
                </div>
              </>
            )}
            
            {/* Break Timer Content */}
            {isOnBreak && (
              <>
                <div className="p-6 pb-0">
                  <h2 className="text-white text-center text-xl font-semibold pr-8">
                    Break Time ☕
                  </h2>
                </div>
                <div className="p-6 space-y-6">
                  <div className="text-center">
                    <div className="text-4xl font-mono font-bold text-green-400 mb-4">
                      {formatTime(breakTimeLeft)}
                    </div>
                    <p className="text-slate-300">
                      Take a deep breath and relax. You'll be back to work soon!
                    </p>
                  </div>
                </div>
              </>
            )}
            
            {/* Break End Alert Content */}
            {showBreakEndAlert && (
              <>
                <div className="p-6 pb-0">
                  <h2 className="text-white text-center text-xl font-semibold">
                    Break over - back to work! 💪
                  </h2>
                </div>
                <div className="p-6 space-y-4">
                  <div className="text-center">
                    <p className="text-slate-300 mb-4">
                      Hope you feel refreshed! Let's continue with your sprint.
                    </p>
                    <Button 
                      onClick={closeBreakEndAlert}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                      autoFocus
                    >
                      Resume Sprint
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Sprint Completion Modal - rendered above all other content */}
      {showCompletionModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center pointer-events-auto z-[99999]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="completion-modal-title"
          onKeyDown={(e) => {
            // Esc key closes modal
            if (e.key === 'Escape') {
              dismissCompletion();
            }
          }}
        >
          {/* Dim overlay - same as break modal */}
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99998]" />
          
          {/* Centered modal panel - same structure as break modal */}
          <div className="relative bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-w-md w-full mx-4 z-[99999]">
            {(() => {
              console.log("Completion Modal Centered OK");
              return null;
            })()}
            
            {/* Modal Content */}
            <div className="p-6 pb-0">
              <h2 id="completion-modal-title" className="text-white text-center text-2xl font-bold">
                🎉 Sprint Complete!
              </h2>
            </div>
            <div className="p-6 space-y-6">
              <div className="text-center">
                <p className="text-slate-300 text-lg">
                  Great work! You've completed your sprint.
                </p>
              </div>
              <div className="flex justify-center">
                <Button 
                  onClick={dismissCompletion}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-3 text-base font-medium"
                  autoFocus
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};
// Sprint Timer Component
const SprintTimer = ({ sprint, onComplete, focusMode, setFocusMode, onViewProgress, onSetActiveSprint, activeSprint, pendingCompletion, onCompleteLastSprint }) => {
  const timer = useTimerService(sprint, activeSprint);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [selectedTaskId, setSelectedTaskId] = useState(null); // Manual selection
  const [tasks, setTasks] = useState(sprint.tasks || []);
  const [sprintStarted, setSprintStarted] = useState(false);
  const [isResuming, setIsResuming] = useState(false);

  // Retrieve sprint meta (goal + duration) with safe fallbacks
  const getSprintMeta = () => {
    // 1) Prefer in-memory sprint data
    const goal = sprint?.goal ?? activeSprint?.goal ?? null;
    const durationMin = sprint?.duration_minutes ?? null;
    if (goal && durationMin != null) return { goal, durationMin };
    
    // 2) Fallback to localStorage
    try {
      const raw = localStorage.getItem('ttx:lastSprintMeta');
      if (raw) {
        const parsed = JSON.parse(raw);
        return { 
          goal: parsed?.goal ?? goal,
          durationMin: typeof parsed?.durationMin === 'number' ? parsed.durationMin : durationMin 
        };
      }
    } catch (e) {
      // Silent fail
    }
    
    return { goal: goal ?? null, durationMin: durationMin ?? null };
  };

  const { goal: sprintGoal, durationMin } = getSprintMeta();


  // Auto-skip break function - REMOVED for atomic break behavior
  // Break modal requires explicit user action - handled by root BreakUIController
  
  // Handle break trigger from TimerService - REMOVED for event-based system
  // Using root BreakUIController with 'ttx:break-start' event listener instead

  // Format time for break display - moved to BreakUIController
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Initialize from activeSprint if resuming (not for new sprints from setup)
  useEffect(() => {
    if (activeSprint && activeSprint.id === sprint.id && !activeSprint.completed && activeSprint.remainingMs < activeSprint.durationMs) {
      setIsResuming(true);
      setSprintStarted(true);
      setTasks(activeSprint.tasks || sprint.tasks || []);
      setFocusMode(activeSprint.focusMode || false);
      
      // For paused sprints, use exact remainingMs without recomputing elapsed time
      // For running sprints, compute fresh elapsed time only if needed
      let hydrationData = activeSprint;
      
      if (!activeSprint.isPaused && activeSprint.lastTickAt) {
        // Only compute elapsed time if timer was running (not paused)
        const now = Date.now();
        const elapsedSinceLastTick = now - activeSprint.lastTickAt;
        const remainingMs = Math.max(0, activeSprint.remainingMs - elapsedSinceLastTick);
        
        hydrationData = {
          ...activeSprint,
          remainingMs: remainingMs
        };
      }
      
      // Hydrate TimerService with exact remainingMs (logging handled inside hydrate method)
      timer.hydrate(hydrationData);
    }
    // Removed bypass condition - allow Ready to Sprint page to show for new sprints
  }, [activeSprint?.id, sprint.id]); // Only depend on IDs to prevent excessive re-runs





  // Update activeSprint while timer is running (throttled for performance)
  useEffect(() => {
    if (sprintStarted && onSetActiveSprint && timer.timeLeft > 0 && !timer.isPaused) {
      // Throttle activeSprint updates to max 4 times per second (250ms)
      const interval = setInterval(() => {
        const updatedActiveSprint = {
          id: sprint.id,
          startedAt: activeSprint?.startedAt || Date.now(),
          durationMs: sprint.duration_minutes * 60 * 1000,
          remainingMs: timer.remainingMs, // Use precise remainingMs from TimerService
          isPaused: timer.isPaused,
          lastTickAt: Date.now(),
          tasks: tasks,
          goal: sprint.goal,
          focusMode: focusMode,
          completed: false,
          break_config: sprint.break_config,
          breaksFired: activeSprint?.breaksFired || [] // Preserve existing breaksFired
        };
        
        onSetActiveSprint(updatedActiveSprint);
      }, 250); // Update max 4 times per second to avoid UI stutter
      
      return () => clearInterval(interval);
    }
  }, [sprintStarted, timer.timeLeft, timer.isPaused, tasks, focusMode]);

  // Autosave tasks function
  const autosaveTasks = async (updatedTasks) => {
    try {
      await privateAPI.put(`/sprints/${sprint.id}/tasks`, {
        tasks: updatedTasks.map((task, index) => ({
          id: task.id,
          title: task.title,
          description: task.description || '',
          estimated_minutes: task.estimated_minutes,
          status: task.status,
          order_index: index
        }))
      });
    } catch (error) {
      console.error('Failed to autosave tasks:', error);
    }
  };

  // Update tasks with autosave
  const updateTasks = (newTasks) => {
    setTasks(newTasks);
    autosaveTasks(newTasks);
  };

  // Handle current task change
  const handleCurrentTaskChange = (taskId) => {
    setSelectedTaskId(taskId);
    
    // Analytics event
    trackEvent('subtask_selected', {
      subtask_id: taskId,
      sprint_id: sprint.id,
      timestamp: new Date().toISOString()
    });
  };

  // Get current task (manual selection takes priority)
  const getCurrentTask = () => {
    if (selectedTaskId) {
      const selectedTask = tasks.find(task => task.id === selectedTaskId);
      if (selectedTask && selectedTask.status !== 'completed') {
        return selectedTask;
      }
    }
    // Fall back to first incomplete task
    return tasks.find(task => task.status !== 'completed');
  };

  const currentTask = getCurrentTask();

  // Listen for focus mode events
  useEffect(() => {
    const handleFocusMode = () => {
      if (sprintStarted && timer.timeLeft > 0) {
        setFocusMode(true);
      }
    };

    window.addEventListener('startFocusMode', handleFocusMode);
    
    return () => {
      window.removeEventListener('startFocusMode', handleFocusMode);
    };
  }, [sprintStarted, timer.timeLeft, setFocusMode]);

  const handleStartSprint = async () => {
    try {
      await privateAPI.post(`/sprints/${sprint.id}/start`);
      
      // Create and persist activeSprint
      const now = Date.now();
      const durationMs = sprint.duration_minutes * 60 * 1000;
      const activeSprintData = {
        id: sprint.id,
        startedAt: now,
        durationMs: durationMs,
        remainingMs: durationMs,
        isPaused: false,
        lastTickAt: now,
        tasks: tasks,
        goal: sprint.goal,
        focusMode: false,
        completed: false,
        break_config: sprint.break_config,
        breaksFired: activeSprint?.breaksFired || [] // Preserve existing breaksFired
      };
      
      // Persist to parent component and localStorage
      if (onSetActiveSprint) {
        onSetActiveSprint(activeSprintData);
      }
      
      setSprintStarted(true);
      timer.startNew(durationMs, activeSprintData);
    } catch (error) {
      console.error('Failed to start sprint:', error);
      console.error('SPRINT_START_ERROR:', error.message || 'Unknown error during sprint start');
    }
  };

  const markTaskComplete = (taskId) => {
    const updatedTasks = tasks.map(task => 
      task.id === taskId ? { ...task, status: 'completed', completedAt: new Date() } : task
    );
    setTasks(updatedTasks);
    autosaveTasks(updatedTasks);
    
    // Analytics event
    trackEvent('subtask_completed', {
      subtask_id: taskId,
      timestamp: new Date().toISOString()
    });
    
    // If completed task was manually selected, clear selection to auto-select next
    if (selectedTaskId === taskId) {
      setSelectedTaskId(null);
    }
  };

  const undoTaskComplete = (taskId) => {
    const updatedTasks = tasks.map(task => 
      task.id === taskId ? { ...task, status: 'pending', completedAt: null } : task
    );
    setTasks(updatedTasks);
    autosaveTasks(updatedTasks);
    
    // Analytics event
    trackEvent('subtask_undone', {
      subtask_id: taskId,
      timestamp: new Date().toISOString()
    });
  };

  const selectTask = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (task && task.status !== 'completed') {
      setSelectedTaskId(taskId);
      
      // Analytics event
      trackEvent('subtask_selected', {
        subtask_id: taskId,
        timestamp: new Date().toISOString()
      });
    }
  };

  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  const handleComplete = () => {
    setShowCompleteDialog(true);
  };

  const confirmComplete = () => {
    setShowCompleteDialog(false);
    timer.stop();
    onComplete({ ...sprint, tasks });
  };

  const cancelComplete = () => {
    setShowCompleteDialog(false);
  };

  // Handle pause with activeSprint update
  const handlePause = () => {
    timer.pause();
    
    // Update activeSprint state
    if (onSetActiveSprint && activeSprint) {
      const pausedActiveSprint = {
        ...activeSprint,
        isPaused: true,
        remainingMs: timer.getState().remainingMs,
        lastTickAt: performance.now()
      };
      onSetActiveSprint(pausedActiveSprint);
    }
  };

  // Handle resume with activeSprint update  
  const handleResume = () => {
    timer.resume();
    
    // Update activeSprint state
    if (onSetActiveSprint && activeSprint) {
      const resumedActiveSprint = {
        ...activeSprint,
        isPaused: false,
        lastTickAt: performance.now()
      };
      onSetActiveSprint(resumedActiveSprint);
    }
  };

  // Render Focus Mode if active
  if (focusMode) {
    return (
      <FocusMode 
        sprint={sprint}
        timer={timer}
        tasks={tasks}
        currentTask={currentTask}
        onTaskComplete={markTaskComplete}
        onTaskUndo={undoTaskComplete}
        onTasksUpdate={updateTasks}
        onCurrentTaskChange={handleCurrentTaskChange}
        isTimerRunning={timer.isRunning}
        onComplete={(sprintFromFocus, focusedSeconds, completed) => {
          setFocusMode(false);
          onComplete({ ...sprintFromFocus, tasks });
        }}
        onExit={(action) => {
          setFocusMode(false);
          if (action === 'new') {
            onComplete({ ...sprint, tasks });
          }
        }}
        onExitFocus={() => {
          setFocusMode(false);
        }}
      />
    );
  }

  if (!sprintStarted) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Navigation Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-2">
            <span className="text-xl font-semibold text-white">TimeTactix Sprint</span>
          </div>
          <div className="flex items-center space-x-4">
            {pendingCompletion && (
              <Button 
                onClick={onCompleteLastSprint}
                variant="outline" 
                size="sm"
                className="bg-orange-600/20 border-orange-500/30 text-orange-300 hover:text-white hover:bg-orange-600"
              >
                <FileCheck className="h-4 w-4 mr-2" />
                Complete Last Sprint
              </Button>
            )}
            <Button 
              onClick={onViewProgress} 
              variant="ghost" 
              size="sm"
              className="text-slate-300 hover:text-white"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Sprint Journal
            </Button>
          </div>
        </div>
        
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-2">Ready to Sprint?</h1>
          <p className="text-slate-400">Your AI-generated task breakdown is ready</p>
          {console.log("Preparation Page Restored")}
        </div>

        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white">Goal: {sprint.goal}</CardTitle>
            <Badge variant="secondary" className="w-fit">{sprint.duration_minutes} minutes</Badge>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <h3 className="text-white font-medium">Task Breakdown:</h3>
              <EditableTaskList
                tasks={tasks}
                onTasksUpdate={updateTasks}
                sprintId={sprint.id}
                isTimerRunning={false}
                onTaskComplete={markTaskComplete}
                onTaskUndo={undoTaskComplete}
              />
            </div>

            <Button
              onClick={handleStartSprint}
              className="w-full mt-6 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium h-12"
            >
              <Play className="h-4 w-4 mr-2" />
              Start Sprint Timer
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Navigation Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-2">
          <span className="text-xl font-semibold text-white">TimeTactix Sprint</span>
        </div>
        <div className="flex items-center space-x-4">
          {pendingCompletion && (
            <Button 
              onClick={onCompleteLastSprint}
              variant="outline" 
              size="sm"
              className="bg-orange-600/20 border-orange-500/30 text-orange-300 hover:text-white hover:bg-orange-600"
            >
              <FileCheck className="h-4 w-4 mr-2" />
              Complete Last Sprint
            </Button>
          )}
          <Button 
            onClick={() => {
              // Pause timer before navigating to journal
              if (sprintStarted && timer && !timer.isPaused) {
                timer.pauseTimer();
              }
              onViewProgress();
            }}
            variant="ghost" 
            size="sm"
            className="text-slate-300 hover:text-white"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Sprint Journal
          </Button>
        </div>
      </div>
      
      {/* Main Timer Display */}





      {/* Focus Mode Button */}
      <div className="flex justify-center">
        <FocusModeDialog 
          onStart={() => window.dispatchEvent(new CustomEvent('startFocusMode'))}
          disabled={!timer.hasStarted || timer.remainingMs <= 0}
        />
      </div>

      {/* Timer Display */}
      <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
        <CardContent className="p-8 text-center">
          <div className="text-6xl font-mono font-bold text-white mb-4">
            {timer.formatTime()}
          </div>
          {/* Unified Progress Bar - always left-to-right fill */}
          <ProgressBar 
            totalMs={sprint && sprint.duration_minutes ? sprint.duration_minutes * 60 * 1000 : 0}
            elapsedMs={sprint && sprint.duration_minutes ? 
              (sprint.duration_minutes * 60 * 1000 - timer.remainingMs) : 
              0
            }
            isPaused={timer.isPaused}
            className="w-full mb-4"
          />
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-4">
            {!timer.hasStarted ? (
              <Button 
                type="button"
                onClick={timer.start} 
                className="bg-green-600 hover:bg-green-700 text-white border-0 px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-base w-full sm:w-auto"
              >
                <Play className="h-4 w-4 mr-2" />
                Start
              </Button>
            ) : timer.isPaused ? (
              <Button 
                type="button"
                onClick={handleResume} 
                className="bg-green-600 hover:bg-green-700 text-white border-0 px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-base w-full sm:w-auto"
              >
                <Play className="h-4 w-4 mr-2" />
                Resume
              </Button>
            ) : (
              <Button 
                type="button"
                onClick={handlePause} 
                className="bg-amber-600 hover:bg-amber-700 text-white border-0 px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-base w-full sm:w-auto"
              >
                <Pause className="h-4 w-4 mr-2" />
                Pause
              </Button>
            )}
            <Button 
              onClick={handleComplete} 
              className="bg-red-600 hover:bg-red-700 text-white border-0 px-3 py-2 text-xs sm:px-4 sm:py-3 sm:text-sm w-full sm:w-auto"
            >
              <Square className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="truncate">Complete Sprint</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Goal + Current Task Card (always visible) */}
      <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
        {/* Goal Header (always visible) */}
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="max-w-[80%] min-w-0 flex-1">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Goal</div>
              <div className="text-base text-slate-100 leading-snug line-clamp-2 break-words">
                {sprintGoal && sprintGoal.length > 0 ? sprintGoal : 'No goal set'}
              </div>
            </div>
            {typeof durationMin === 'number' && (
              <span className="shrink-0 rounded-md border border-slate-600/60 px-2 py-1 text-xs text-slate-200">
                {durationMin} minutes
              </span>
            )}
          </div>
        </CardHeader>

        {/* Current Task Body (conditional) */}
        {currentTask && (
          <>
            <div className="px-6 pb-2">
              <div className="flex items-center space-x-2 text-slate-400">
                <Target className="h-4 w-4" />
                <span className="text-sm font-medium">Current Task</span>
              </div>
            </div>
            <CardContent className="pt-2">
              <div className="space-y-4">
                <h3 className="text-xl text-white font-medium">{currentTask.title}</h3>
                {currentTask.description && (
                  <p className="text-slate-400">{currentTask.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <Badge variant="secondary">{currentTask.estimated_minutes} minutes</Badge>
                  <Button
                    onClick={() => markTaskComplete(currentTask.id)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark Complete
                  </Button>
                </div>
              </div>
            </CardContent>
          </>
        )}
      </Card>


      {/* Task List */}
      <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-white">All Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          <EditableTaskList
            tasks={tasks}
            onTasksUpdate={updateTasks}
            currentTaskId={currentTask?.id}
            onCurrentTaskChange={handleCurrentTaskChange}
            sprintId={sprint.id}
            isTimerRunning={timer.isRunning}
            onTaskComplete={markTaskComplete}
            onTaskUndo={undoTaskComplete}
          />
        </CardContent>
      </Card>

      {/* Complete Sprint Confirmation Dialog */}
      {showCompleteDialog && (
        <Dialog open={showCompleteDialog} onOpenChange={(open) => { if (!open) cancelComplete(); }}>
          <DialogContent
            data-testid="complete-sprint-confirm-dialog"
            className="bg-slate-800 border-slate-700"
          >
            <DialogHeader>
              <DialogTitle className="text-white">Complete sprint?</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-300">
                You've focused for {formatMs(Math.max(0, (sprint.duration_minutes * 60 * 1000) - timer.getState().remainingMs))}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  data-testid="complete-sprint-confirm-button"
                  onClick={confirmComplete}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 w-full sm:w-auto"
                >
                  Complete & Save
                </Button>
                <Button
                  data-testid="complete-sprint-cancel-button"
                  onClick={cancelComplete}
                  variant="ghost"
                  className="text-white w-full sm:w-auto"
                >
                  Continue
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
};

// Sprint Completion Component
const SprintCompletion = ({ sprint, onNewSprint, onViewProgress, pendingCompletion, onSavePendingCompletion, onClearPendingCompletion }) => {
  // Initialize from pendingCompletion if available, otherwise use defaults
  const [finished, setFinished] = useState(pendingCompletion?.finished ?? false);
  const [moodRating, setMoodRating] = useState(pendingCompletion?.moodRating ?? 3);
  // Structured Win / Next Move state — backward compatible with legacy pendingCompletion.proofNote
  const legacyProofNote = pendingCompletion?.proofNote ?? '';
  const [winNote, setWinNote] = useState(pendingCompletion?.winNote ?? '');
  const [nextMove, setNextMove] = useState(pendingCompletion?.nextMove ?? '');

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Retrieve sprint meta (goal + duration) from multiple sources
  const getSprintMeta = () => {
    // 1) Prefer in-memory sprint data
    const goal = sprint?.goal ?? null;
    const durationMin = sprint?.duration_minutes ?? null;
    if (goal && durationMin != null) return { goal, durationMin };
    
    // 2) Fallback to localStorage
    try {
      const raw = localStorage.getItem('ttx:lastSprintMeta');
      if (raw) {
        const parsed = JSON.parse(raw);
        return { goal: parsed.goal || null, durationMin: parsed.durationMin || null };
      }
    } catch (e) {
      // Silent fail
    }
    
    return { goal: null, durationMin: null };
  };

  const { goal: sprintGoal, durationMin } = getSprintMeta();

  // Editable subtasks for the review screen (prefer live sprint.tasks, fallback to pendingCompletion)
  const initialReviewTasks = Array.isArray(sprint?.tasks) && sprint.tasks.length > 0
    ? sprint.tasks
    : (Array.isArray(pendingCompletion?.tasks) ? pendingCompletion.tasks : []);
  const [reviewTasks, setReviewTasks] = useState(initialReviewTasks);
  const completedCount = reviewTasks.filter(t => t?.status === 'completed').length;

  // Persist reviewTasks to backend using the same tasks PUT endpoint as autosave
  const persistReviewTasks = async (nextTasks) => {
    try {
      await privateAPI.put(`/sprints/${sprint.id}/tasks`, {
        tasks: nextTasks.map((task, index) => ({
          id: task.id,
          title: task.title,
          description: task.description || '',
          estimated_minutes: task.estimated_minutes,
          status: task.status,
          order_index: index
        }))
      });
    } catch (error) {
      console.error('Failed to persist review tasks:', error);
    }
  };

  const applyReviewTasksChange = (nextTasks) => {
    setReviewTasks(nextTasks);
    // Persist to pendingCompletion so reload/Complete-Last-Sprint keeps state
    if (pendingCompletion && pendingCompletion.sprintId === sprint.id) {
      onSavePendingCompletion({
        ...pendingCompletion,
        tasks: nextTasks,
        timestamp: Date.now()
      });
    }
    persistReviewTasks(nextTasks);
  };

  const handleReviewTaskComplete = (taskId) => {
    const next = reviewTasks.map(t =>
      t.id === taskId ? { ...t, status: 'completed', completedAt: new Date().toISOString() } : t
    );
    applyReviewTasksChange(next);
  };

  const handleReviewTaskUndo = (taskId) => {
    const next = reviewTasks.map(t =>
      t.id === taskId ? { ...t, status: 'pending', completedAt: null } : t
    );
    applyReviewTasksChange(next);
  };

  const handleReviewTaskDelete = (taskId) => {
    const next = reviewTasks.filter(t => t.id !== taskId);
    applyReviewTasksChange(next);
  };

  // Create or update pendingCompletion when component mounts or sprint changes
  useEffect(() => {
    if (!pendingCompletion || pendingCompletion.sprintId !== sprint.id) {
      const newPendingCompletion = {
        sprintId: sprint.id,
        tasks: sprint.tasks || [],
        timestamp: Date.now(),
        finished: finished,
        moodRating: moodRating,
        winNote: winNote,
        nextMove: nextMove
      };
      onSavePendingCompletion(newPendingCompletion);
    }
  }, [sprint.id]);

  // Save state whenever form values change
  useEffect(() => {
    if (pendingCompletion && pendingCompletion.sprintId === sprint.id) {
      const updatedPendingCompletion = {
        ...pendingCompletion,
        finished: finished,
        moodRating: moodRating,
        winNote: winNote,
        nextMove: nextMove,
        timestamp: Date.now()
      };
      onSavePendingCompletion(updatedPendingCompletion);
    }
  }, [finished, moodRating, winNote, nextMove]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const win = (winNote || '').trim();
      const nm = (nextMove || '').trim();
      // Compatibility mirror for legacy consumers of proof_note
      const mirrorLines = [];
      if (win) mirrorLines.push(`Win: ${win}`);
      if (nm) mirrorLines.push(`Next Move: ${nm}`);
      const compatNote = mirrorLines.join('\n') || null;

      await privateAPI.post(`/sprints/${sprint.id}/complete`, {
        finished,
        mood_rating: moodRating,
        proof_note: compatNote,
        win_note: win || null,
        next_move: nm || null
      });
      
      // Clear pendingCompletion after successful save
      onClearPendingCompletion();
      
      onViewProgress();
    } catch (error) {
      console.error('Error completing sprint:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscard = () => {
    // Clear pendingCompletion without saving to journal
    onClearPendingCompletion();
    onNewSprint();
  };

  const moodLabels = ['😫', '😔', '😐', '😊', '😆'];

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6 sm:space-y-8">
      {/* Navigation Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-2">
          <span className="text-xl font-semibold text-white">TimeTactix Sprint</span>
        </div>
        <div className="flex items-center space-x-4">
          <Button 
            onClick={onViewProgress} 
            variant="ghost" 
            size="sm"
            className="text-slate-300 hover:text-white"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Sprint Journal
          </Button>
        </div>
      </div>
      
      <div className="text-center space-y-4">
        <LogoTile 
          size="h-12 w-12 sm:h-16 sm:w-16" 
          cornerRadius="rounded-xl"
          className="mx-auto"
        />
        <h1 className="text-2xl sm:text-3xl font-bold text-white px-4">Sprint Complete!</h1>
        <p className="text-slate-400 text-sm sm:text-base px-4">How did it go?</p>
      </div>

      <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm mx-2 sm:mx-0">
        <CardHeader className="pb-4">
          <CardTitle className="text-white text-lg sm:text-xl">Session Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Sprint Goal Display */}
          <div className="mt-2 mb-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="max-w-[80%] min-w-0 flex-1">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Goal</div>
              <div className="text-base text-slate-100 leading-snug break-words line-clamp-2">
                {sprintGoal && sprintGoal.length > 0 ? sprintGoal : 'No goal set'}
              </div>
            </div>
            {typeof durationMin === 'number' && (
              <span className="shrink-0 rounded-md border border-slate-600/60 px-2 py-1 text-xs text-slate-200">
                {durationMin} minutes
              </span>
            )}
          </div>
          
          <div className="text-center">
            <h3 className="text-base sm:text-lg text-white mb-3 px-2">Did you finish your goal?</h3>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-2">
              <Button
                onClick={() => setFinished(true)}
                className={finished 
                  ? "bg-green-600 hover:bg-green-700 text-white flex-1 sm:flex-none" 
                  : "bg-white text-slate-800 border border-slate-300 hover:bg-gray-50 hover:shadow-md transition-all duration-200 flex-1 sm:flex-none"
                }
              >
                ✅ Yes, finished!
              </Button>
              <Button
                onClick={() => setFinished(false)}
                className={!finished 
                  ? "bg-green-600 hover:bg-green-700 text-white flex-1 sm:flex-none" 
                  : "bg-white text-slate-800 border border-slate-300 hover:bg-gray-50 hover:shadow-md transition-all duration-200 flex-1 sm:flex-none"
                }
              >
                ⏱️ Made progress
              </Button>
            </div>
          </div>

          <div className="px-2">
            <h3 className="text-white font-medium mb-4 text-sm sm:text-base">How are you feeling?</h3>
            <div className="flex justify-center space-x-2 sm:space-x-4">
              {moodLabels.map((emoji, index) => (
                <button
                  key={index}
                  onClick={() => setMoodRating(index + 1)}
                  className={`text-2xl sm:text-4xl p-2 sm:p-3 rounded-full transition-all duration-200 ${
                    moodRating === index + 1 
                      ? 'scale-110 sm:scale-125 bg-amber-100/20 shadow-lg' 
                      : 'hover:scale-105 sm:hover:scale-110 hover:bg-slate-700/50 opacity-70'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          {reviewTasks.length > 0 && (
            <div
              className="px-2"
              data-testid="review-subtask-summary"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium text-sm sm:text-base">
                  Sprint task summary
                </h3>
                <span
                  className="text-xs text-slate-400"
                  data-testid="review-subtask-count"
                >
                  {completedCount} of {reviewTasks.length} completed
                </span>
              </div>
              <ul
                className="space-y-2 rounded-lg border border-slate-700/60 bg-slate-800/40 p-2 sm:p-3"
                data-testid="review-subtask-list"
              >
                {reviewTasks.map((task, idx) => {
                  const isDone = task?.status === 'completed';
                  const title = task?.title || task?.description || `Task ${idx + 1}`;
                  const mins = typeof task?.estimated_minutes === 'number' ? task.estimated_minutes : null;
                  return (
                    <li
                      key={task?.id || `review-task-${idx}`}
                      data-testid={`review-subtask-row-${idx}`}
                      data-status={isDone ? 'completed' : 'pending'}
                      className={`group flex items-center gap-3 rounded-md px-2 sm:px-3 py-2 border transition-colors ${
                        isDone
                          ? 'border-green-600/30 bg-green-900/10'
                          : 'border-slate-700/40 bg-slate-900/30 hover:border-slate-600'
                      }`}
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                          isDone
                            ? 'border-green-500 bg-green-600 text-white'
                            : 'border-slate-500 bg-transparent text-transparent'
                        }`}
                        aria-hidden="true"
                      >
                        {isDone ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="h-3 w-3"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 111.42-1.42L8.5 12.08l6.79-6.79a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : null}
                      </span>
                      <span
                        className={`text-sm sm:text-[0.95rem] flex-1 min-w-0 leading-snug break-words ${
                          isDone ? 'text-slate-200 line-through decoration-slate-500/60' : 'text-slate-300'
                        }`}
                      >
                        {title}
                      </span>
                      <div className="shrink-0 flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-1.5">
                        {isDone ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.currentTarget.blur();
                              handleReviewTaskUndo(task.id);
                            }}
                            data-testid={`review-subtask-undo-${idx}`}
                            className="order-2 sm:order-none inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] leading-none bg-slate-700 text-slate-200 hover:bg-slate-600 active:bg-slate-600 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity"
                            aria-label={`Undo ${title}`}
                          >
                            <Undo className="h-3 w-3" />
                            Undo
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.currentTarget.blur();
                              handleReviewTaskComplete(task.id);
                            }}
                            data-testid={`review-subtask-complete-${idx}`}
                            className="order-2 sm:order-none inline-flex items-center rounded px-2 py-0.5 text-[11px] leading-none bg-green-600 text-white hover:bg-green-500 active:bg-green-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity"
                            aria-label={`Complete ${title}`}
                          >
                            Complete
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.currentTarget.blur();
                            handleReviewTaskDelete(task.id);
                          }}
                          data-testid={`review-subtask-delete-${idx}`}
                          className="order-3 sm:order-none p-1 rounded text-slate-400 hover:text-red-400 hover:bg-slate-700/60 inline-flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity"
                          aria-label={`Delete ${title}`}
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </button>
                        {mins != null && (
                          <span className="order-1 sm:order-none shrink-0 rounded-md border border-slate-600/60 px-1.5 py-0.5 text-[11px] text-slate-300">
                            {mins}m
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="space-y-4 px-2">
            <div>
              <label className="text-white font-medium mb-2 block text-sm sm:text-base">
                Win (optional)
              </label>
              <Textarea
                data-testid="completion-win-input"
                placeholder="What did you complete?"
                value={winNote}
                onChange={(e) => setWinNote(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white placeholder-slate-300 text-sm sm:text-base min-h-[60px]"
              />
            </div>
            <div>
              <label className="text-white font-medium mb-2 block text-sm sm:text-base">
                Next Move (optional)
              </label>
              <Textarea
                data-testid="completion-next-move-input"
                placeholder="What will you do first for your next Sprint?"
                value={nextMove}
                onChange={(e) => setNextMove(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white placeholder-slate-300 text-sm sm:text-base min-h-[60px]"
              />
            </div>
            {!winNote && !nextMove && legacyProofNote && (
              <p className="text-[11px] text-slate-500">
                Previous note is preserved and can still be viewed in the Sprint Journal.
              </p>
            )}
          </div>

          <div className="px-2 space-y-3">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-sm sm:text-base py-2 sm:py-3"
            >
              {isSubmitting ? 'Saving...' : 'Save & Continue'}
            </Button>
            
            <Button
              onClick={handleDiscard}
              disabled={isSubmitting}
              variant="ghost"
              className="w-full text-slate-400 hover:text-white text-sm"
            >
              Discard & Start New
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Progress Dashboard Component
const ProgressDashboard = ({ onNewSprint, onLogout, onResumeSprint, activeSprint, pendingCompletion, onCompleteLastSprint }) => {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  
  // Sprint Journal local delete modal state - scoped only to Journal
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Status toggle confirmation modal state
  const [isStatusConfirmOpen, setIsStatusConfirmOpen] = useState(false);
  const [statusToggleTarget, setStatusToggleTarget] = useState(null); // {sessionId, currentFinished}

  // Sprint Journal pagination state
  const [journalVisibleCount, setJournalVisibleCount] = useState(25);

  // Mood emoji picker state for journal entries
  const [moodPickerSessionId, setMoodPickerSessionId] = useState(null);
  const moodPickerRef = useRef(null);

  // Close mood picker when clicking outside
  useEffect(() => {
    if (!moodPickerSessionId) return;
    const handleClickOutside = (e) => {
      if (moodPickerRef.current && !moodPickerRef.current.contains(e.target)) {
        setMoodPickerSessionId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [moodPickerSessionId]);

  // Compute dashboard metrics from sessions (updates instantly when sessions change)
  const dashboardStats = useMemo(() => computeSprintMetrics(sessions), [sessions]);

  // Reset delete modal state on component mount to prevent auto-opening
  useEffect(() => {
    setSelectedSessionId(null);
    setIsDeleteOpen(false);
  }, []);

  // Force fresh fetch on mount and after navigation
  useEffect(() => {
    loadJournalData(true); // Force fresh fetch
  }, []);

  const loadJournalData = async (forceFresh = false) => {
    setLoading(true);
    try {
      // Add cache-busting parameter if forcing fresh fetch
      const cacheParam = forceFresh ? `_t=${Date.now()}` : '';
      
      // Load user's private stats and sessions concurrently
      // Request up to 5000 sessions (matches backend default)
      const [statsResponse, sessionsResponse] = await Promise.all([
        privateAPI.get(`/stats${cacheParam ? '?' + cacheParam : ''}`).catch(() => ({ data: null })),
        privateAPI.get(`/sessions?limit=5000&skip=0${cacheParam ? '&' + cacheParam : ''}`).catch(() => ({ data: [] }))
      ]);

      setStats(statsResponse.data);
      
      // Normalize and filter sessions before setting state
      // Store ALL returned sessions (no truncation)
      const normalizedSessions = (sessionsResponse.data || [])
        .map(normalizeSession)
        .filter(session => session && session.id);
      
      setSessions(normalizedSessions);
      
      // Reset selectedSessionId if it no longer exists in the updated list
      if (selectedSessionId && !normalizedSessions.find(s => s.id === selectedSessionId)) {
        setSelectedSessionId(null);
        setIsDeleteOpen(false);
      }
      
    } catch (error) {
      console.error('Error loading journal data:', error);
      // Set empty state on error
      setStats(null);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };



  const handleSessionClick = (session) => {
    setSelectedSession(session);
    setIsSessionModalOpen(true);
  };

  const handleUpdateSession = async (sessionId, updates) => {
    try {
      const userId = getUserId();
      await privateAPI.patch(`/sessions/${sessionId}`, {
        user_id: userId,
        ...updates
      });
      
      // Update the session in local state (immutable update)
      setSessions(prevSessions => prevSessions.map(session => 
        session.id === sessionId 
          ? { ...session, ...updates }
          : session
      ));
      
      // Update selected session if it's the one being edited
      if (selectedSession?.id === sessionId) {
        setSelectedSession(prev => ({ ...prev, ...updates }));
      }
    } catch (error) {
      console.error('Error updating session:', error);
      throw error;
    }
  };

  // Toggle status between Completed and In Progress with confirmation
  const handleToggleStatus = async (sessionId, currentFinished) => {
    setStatusToggleTarget({ sessionId, currentFinished });
    setIsStatusConfirmOpen(true);
  };

  // Confirm status toggle
  const confirmStatusToggle = async () => {
    if (!statusToggleTarget) return;
    
    const { sessionId, currentFinished } = statusToggleTarget;
    const nextFinished = !currentFinished;
    
    try {
      await handleUpdateSession(sessionId, { finished: nextFinished });
    } catch (e) {
      // Error already logged in handleUpdateSession; close modal regardless to avoid stuck state
    }
    
    setIsStatusConfirmOpen(false);
    setStatusToggleTarget(null);
  };

  // Cancel status toggle
  const cancelStatusToggle = () => {
    setIsStatusConfirmOpen(false);
    setStatusToggleTarget(null);
  };

  // Delete session handler with permanent deletion
  const handleDeleteSession = async (sessionId) => {
    try {
      // Atomic delete - remove exactly one session by stable id
      await privateAPI.delete(`/sessions/${sessionId}`);
      
      // Force fresh fetch from server to ensure consistency and prevent reappearance
      await loadJournalData(true);
      
      // Close modal and reset state
      setIsDeleteOpen(false);
      setSelectedSessionId(null);
      
    } catch (error) {
      console.error('Error deleting session:', error);
      // On error, force fresh fetch to maintain consistency
      await loadJournalData(true);
    }
  };

  // Show delete confirmation modal - ONLY from explicit trash icon click
  const handleDeleteClick = (event, session) => {
    event.stopPropagation(); // Prevent card click
    setSelectedSessionId(session.id);
    setIsDeleteOpen(true);
  };

  // Close delete confirmation modal
  const handleDeleteCancel = () => {
    setIsDeleteOpen(false);
    setSelectedSessionId(null);
  };

  // Confirm deletion
  const handleDeleteConfirm = async () => {
    if (selectedSessionId) {
      await handleDeleteSession(selectedSessionId);
    }
  };

  // Handle overlay click to close modal
  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleDeleteCancel();
    }
  };

  // Check if session can be deleted (not active/paused) - hardened against undefined
  const canDeleteSession = (session) => {
    // Handle undefined or null session objects
    if (!session || !session.id) {
      return false;
    }
    
    // If no active sprint, all valid sessions can be deleted
    if (!activeSprint || !activeSprint.id) {
      return true;
    }
    
    // Can't delete the currently active/paused sprint
    return session.id !== activeSprint.id;
  };

  // Get selected session for modal display - hardened against undefined
  const getSelectedSession = () => {
    if (!selectedSessionId || !sessions) {
      return null;
    }
    return sessions.find(session => session && session.id === selectedSessionId) || null;
  };

  // Normalize session objects to ensure consistent id field
  const normalizeSession = (session) => {
    if (!session) return null;
    
    return {
      ...session,
      id: session.id || session.session_id, // Map session_id to id if needed
      status: session.finished ? 'completed' : 'in_progress',
      startedAt: session.session_date,
      duration: session.duration_minutes
    };
  };

  // Filter and normalize sessions for rendering
  const getValidSessions = () => {
    if (!sessions || !Array.isArray(sessions)) {
      return [];
    }
    
    return sessions
      .map(normalizeSession)
      .filter(session => session && session.id); // Filter out falsy entries
  };

  // Get sorted sessions (newest → oldest) for journal display
  const getSortedSessions = () => {
    const validSessions = getValidSessions();
    return validSessions.sort((a, b) => {
      const dateA = new Date(a.session_date || a.created_at || a.startedAt);
      const dateB = new Date(b.session_date || b.created_at || b.startedAt);
      return dateB - dateA; // Newest first
    });
  };

  // Get visible sessions for journal (respects pagination)
  const getVisibleSessions = () => {
    const sortedSessions = getSortedSessions();
    return sortedSessions.slice(0, journalVisibleCount);
  };

  // Load more handler
  const handleLoadMore = () => {
    const sortedSessions = getSortedSessions();
    setJournalVisibleCount(prev => Math.min(prev + 25, sortedSessions.length));
  };



  const handleCloseModal = () => {
    setIsSessionModalOpen(false);
    setSelectedSession(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <LogoTile 
              size="h-10 w-10" 
              cornerRadius="rounded-lg"
              className="bg-slate-700"
            />
            <h1 className="text-xl font-bold text-white">TimeTactix Sprint</h1>
          </div>
          <div className="flex items-center space-x-4">
            <Button onClick={onLogout} variant="ghost" size="sm">
              <User className="h-4 w-4 mr-2" />
              Exit App
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-amber-400 mb-1">{dashboardStats.current_streak}</div>
              <div className="text-slate-400 text-sm">Daily Streak</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-400 mb-1">{dashboardStats.weekly_streak}</div>
              <div className="text-slate-400 text-sm">Weekly Streak</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-400 mb-1">{dashboardStats.sessions_this_week}</div>
              <div className="text-slate-400 text-sm">This Week</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-400 mb-1">{dashboardStats.completion_rate}%</div>
              <div className="text-slate-400 text-sm">Completion Rate</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-cyan-400 mb-1">{dashboardStats.consistency_rate}%</div>
              <div className="text-slate-400 text-sm">Consistency Rate</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-400 mb-1">{dashboardStats.focus_hours_logged}h</div>
              <div className="text-slate-400 text-sm">Focus Hours</div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Stats Summary */}
        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <Trophy className="h-5 w-5 text-amber-400" />
              <span>Your Progress</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-400">Daily Streak:</span>
                <span className="text-white font-medium">{dashboardStats.current_streak} days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Weekly Streak:</span>
                <span className="text-white font-medium">{dashboardStats.weekly_streak} weeks</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Total Sessions:</span>
                <span className="text-white font-medium">{dashboardStats.total_sessions}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-400">Consistency Rate:</span>
                <span className="text-white font-medium">{dashboardStats.consistency_rate}% (last 30 days)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Focus Hours:</span>
                <span className="text-white font-medium">{dashboardStats.focus_hours_logged}h total logged</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Completion Rate:</span>
                <span className="text-white font-medium">{dashboardStats.completion_rate}% overall</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Action */}
        <div className="text-center space-y-4">
          <Button
            onClick={onNewSprint}
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium h-14 px-8 text-lg"
          >
            <Play className="h-5 w-5 mr-3" />
            Start New Sprint
          </Button>
          
          {/* Conditional Resume Sprint Button - Only show when paused */}
          {activeSprint && !activeSprint.completed && activeSprint.isPaused && (
            <div className="bg-blue-600/20 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <h3 className="text-white font-medium">Sprint Paused</h3>
                  <p className="text-blue-200 text-sm mt-1">
                    {activeSprint.goal.length > 50 
                      ? `${activeSprint.goal.substring(0, 50)}...` 
                      : activeSprint.goal}
                  </p>
                </div>
                <Button
                  onClick={() => onResumeSprint && onResumeSprint(activeSprint)}
                  variant="outline"
                  className="bg-blue-600 hover:bg-blue-700 text-white border-blue-500"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Resume Sprint
                </Button>
              </div>
            </div>
          )}
          
          {/* Complete Last Sprint Banner */}
          {pendingCompletion && (
            <div className="bg-orange-600/20 border border-orange-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <h3 className="text-white font-medium">Incomplete Sprint Log</h3>
                  <p className="text-orange-200 text-sm mt-1">
                    You have an unfinished sprint completion that needs to be logged.
                  </p>
                </div>
                <Button
                  onClick={onCompleteLastSprint}
                  variant="outline"
                  className="bg-orange-600 hover:bg-orange-700 text-white border-orange-500"
                >
                  <FileCheck className="h-4 w-4 mr-2" />
                  Complete Last Sprint
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Recent Sessions */}
        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center space-x-2">
                <Calendar className="h-5 w-5" />
                <span>Sprint Journal</span>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {/* Static helper text - always visible */}
            <div className="mb-4 text-sm text-slate-400 text-left">
              Stuck on what to do next?<br />
              Briefly tell TactixAI what you just finished and say: &quot;Help me plan my next Sprint.&quot;
            </div>
            
            {sessions.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                No sessions yet. Start your first sprint!
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {getVisibleSessions().map((session) => (
                    <div 
                      key={session.id} 
                      onClick={() => handleSessionClick(session)}
                      className="flex items-center justify-between p-3 sm:p-4 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-600/50 transition-colors duration-200 min-h-0"
                    >
                      <div className="flex-grow min-w-0 pr-3">
                        <div className="text-white font-medium text-sm sm:text-base truncate w-full">{session.goal}</div>
                        <div className="text-slate-400 text-xs sm:text-sm truncate">
                          {formatJournalDate(session.session_date || session.startedAt)} • {session.duration_minutes || session.duration} min
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {session.finished ? (
                          <Badge 
                            className="bg-green-600 text-xs px-2 py-1 whitespace-nowrap cursor-pointer hover:bg-green-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleStatus(session.id, session.finished);
                            }}
                          >
                            Completed
                          </Badge>
                        ) : (
                          <Badge 
                            variant="secondary" 
                            className="text-xs px-2 py-1 whitespace-nowrap cursor-pointer hover:bg-slate-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleStatus(session.id, session.finished);
                            }}
                          >
                            In Progress
                          </Badge>
                        )}
                        {session.mood_rating && (
                          <div className="relative flex-shrink-0">
                            <button
                              type="button"
                              data-testid={`journal-mood-emoji-${session.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMoodPickerSessionId(prev => prev === session.id ? null : session.id);
                              }}
                              className="text-sm sm:text-base p-1 rounded hover:bg-slate-600/60 active:bg-slate-600 transition-colors duration-150 leading-none"
                              aria-label="Change mood for this sprint"
                              title="Change mood"
                            >
                              {['😫', '😔', '😐', '😊', '😆'][session.mood_rating - 1]}
                            </button>
                            {moodPickerSessionId === session.id && (
                              <div
                                ref={moodPickerRef}
                                data-testid={`journal-mood-picker-${session.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute right-0 top-full mt-1 z-50 flex items-center gap-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl px-2 py-1.5"
                              >
                                {['😫', '😔', '😐', '😊', '😆'].map((emoji, index) => {
                                  const newRating = index + 1;
                                  const isSelected = session.mood_rating === newRating;
                                  return (
                                    <button
                                      key={emoji}
                                      type="button"
                                      data-testid={`journal-mood-option-${session.id}-${newRating}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setMoodPickerSessionId(null);
                                        if (!isSelected) {
                                          handleUpdateSession(session.id, { mood_rating: newRating }).catch(() => {});
                                        }
                                      }}
                                      className={`text-lg leading-none rounded p-1 transition-colors duration-150 hover:bg-slate-700 active:bg-slate-600 ${isSelected ? 'bg-slate-700 ring-1 ring-amber-500/60' : ''}`}
                                      aria-label={`Set mood to ${emoji}`}
                                    >
                                      {emoji}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                        {/* Delete Button */}
                        <div className="relative">
                          {canDeleteSession(session) ? (
                            <button
                              onClick={(e) => handleDeleteClick(e, session)}
                              className="p-1 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors duration-200"
                              aria-label={`Delete sprint session: ${session.goal}`}
                              title="Delete this sprint session"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          ) : (
                            <div
                              className="p-1 text-slate-500 cursor-not-allowed"
                              title="Finish or cancel the sprint to delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Load More Button */}
                {getSortedSessions().length > journalVisibleCount && (
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={handleLoadMore}
                      className="w-full max-w-xs bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2.5 px-4 rounded-lg shadow transition"
                    >
                      Load more
                    </button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Session Detail Modal */}
      <SessionDetailModal
        session={selectedSession}
        isOpen={isSessionModalOpen}
        onClose={handleCloseModal}
        onUpdateSession={handleUpdateSession}
        onToggleStatus={handleToggleStatus}
      />

      {/* Delete Confirmation Modal - Scoped to Sprint Journal only */}
      {isDeleteOpen && (
        <div 
          className="fixed inset-0 flex items-center justify-center pointer-events-auto z-[99999]"
          onClick={handleOverlayClick}
        >
          {/* Dim overlay */}
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99998]" />
          
          {/* Centered modal panel */}
          <div 
            className="relative bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-w-md w-full mx-4 z-[99999]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h2 className="text-white text-xl font-semibold mb-4">
                Delete Sprint Session?
              </h2>
              <p className="text-slate-300 mb-2">
                Are you sure you want to delete this sprint session?
              </p>
              {getSelectedSession()?.goal && (
                <p className="text-slate-400 text-sm mb-4 italic">
                  "{getSelectedSession().goal}"
                </p>
              )}
              <p className="text-slate-400 text-sm mb-6">
                This action cannot be undone.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={handleDeleteCancel}
                  variant="outline"
                  className="bg-slate-700/50 border-slate-600 text-white hover:bg-slate-600 flex-1"
                  style={{ pointerEvents: 'auto' }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDeleteConfirm}
                  className="bg-red-600 hover:bg-red-700 text-white flex-1"
                  style={{ pointerEvents: 'auto' }}
                  disabled={selectedSessionId && !canDeleteSession(getSelectedSession())}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Toggle Confirmation Modal */}
      {isStatusConfirmOpen && statusToggleTarget && (
        <div 
          className="fixed inset-0 flex items-center justify-center pointer-events-auto z-[99999]"
          onClick={cancelStatusToggle}
        >
          {/* Dim overlay */}
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99998]" />
          
          {/* Centered modal panel */}
          <div 
            className="relative bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-w-md w-full mx-4 z-[99999]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h2 className="text-white text-xl font-semibold mb-4">
                {!statusToggleTarget.currentFinished ? 
                  'Mark this sprint as "Completed"?' : 
                  'Mark this sprint as "In Progress"?'}
              </h2>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button 
                  onClick={cancelStatusToggle}
                  variant="outline"
                  className="bg-slate-700/50 border-slate-600 text-white hover:bg-slate-600 flex-1"
                  style={{ pointerEvents: 'auto' }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmStatusToggle}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white flex-1"
                  style={{ pointerEvents: 'auto' }}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main App Component  
function App() {
  const { user, login, logout, loading } = useAuth();
  const [currentView, setCurrentView] = useState('setup'); // setup, timer, completion, progress, focus
  const [currentSprint, setCurrentSprint] = useState(null);
  const [activeSprint, setActiveSprint] = useState(null);
  const [pendingCompletion, setPendingCompletion] = useState(null);
  const [focusMode, setFocusMode] = useState(false);

  // Set up TimerService break handlers and re-check test mode on route changes
  useEffect(() => {
    const getCurrentActiveSprint = () => activeSprint;
    const persistActiveSprintFunc = (sprint) => {
      setActiveSprint(sprint);
      persistActiveSprint(sprint);
    };
    const triggerBreak = (sprint) => {
      // Timer paused by TimerService, break UI triggered by event system
      // Keeping this function for compatibility but using event bridge instead
    };
    
    timerService.setBreakHandlers(getCurrentActiveSprint, persistActiveSprintFunc, triggerBreak);
    
    // Re-initialize Break Test Mode detection on route changes
    timerService.initBreakTestMode();
  }, [activeSprint, currentView]); // Re-run when view changes

  // ActiveSprint persistence functions
  const persistActiveSprint = (activeSprintData) => {
    try {
      const userId = getUserId();
      const key = `activeSprint_${userId}`;
      localStorage.setItem(key, JSON.stringify(activeSprintData));
    } catch (error) {
      console.error('Failed to persist activeSprint:', error);
    }
  };

  const loadActiveSprint = () => {
    try {
      const userId = getUserId();
      const key = `activeSprint_${userId}`;
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to load activeSprint:', error);
      return null;
    }
  };

  const clearActiveSprint = () => {
    try {
      const userId = getUserId();
      const key = `activeSprint_${userId}`;
      localStorage.removeItem(key);
      setActiveSprint(null);
    } catch (error) {
      console.error('Failed to clear activeSprint:', error);
    }
  };

  // PendingCompletion persistence functions
  const persistPendingCompletion = (pendingData) => {
    try {
      const userId = getUserId();
      const key = `pendingCompletion_${userId}`;
      localStorage.setItem(key, JSON.stringify(pendingData));
    } catch (error) {
      console.error('Failed to persist pendingCompletion:', error);
    }
  };

  const loadPendingCompletion = () => {
    try {
      const userId = getUserId();
      const key = `pendingCompletion_${userId}`;
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to load pendingCompletion:', error);
      return null;
    }
  };

  const clearPendingCompletion = () => {
    try {
      const userId = getUserId();
      const key = `pendingCompletion_${userId}`;
      localStorage.removeItem(key);
      setPendingCompletion(null);
    } catch (error) {
      console.error('Failed to clear pendingCompletion:', error);
    }
  };

  // Load activeSprint and pendingCompletion on app initialization
  useEffect(() => {
    const stored = loadActiveSprint();
    if (stored && !stored.completed) {
      setActiveSprint(stored);
    }

    const storedPending = loadPendingCompletion();
    if (storedPending) {
      setPendingCompletion(storedPending);
    }
  }, []);

  // Set up window event listeners for focus mode
  useEffect(() => {
    const handleFocusMode = () => setFocusMode(true);
    const handleExitFocus = () => setFocusMode(false);
    
    window.addEventListener('startFocusMode', handleFocusMode);
    window.addEventListener('exitFocusMode', handleExitFocus);
    
    // Brand logo swap confirmation
    console.log("BrandLogo Swap OK - LogoTile Component");
    console.log("Updated files:", [
      "/app/frontend/src/components/LogoTile.js - Central LogoTile component",
      "/app/frontend/src/App.js - Auth/Login view (h-20 w-20)",
      "/app/frontend/src/App.js - Sprint Setup view (h-16 w-16)", 
      "/app/frontend/src/App.js - Sprint Complete view (responsive h-12/h-16)",
      "/app/frontend/src/App.js - Progress Dashboard header (h-10 w-10)",
      "/app/frontend/src/App.js - Loading view (h-16 w-16)"
    ]);
    console.log("Logo behavior: Edge-to-edge fill with object-fit: cover, rounded corners preserved");
    
    return () => {
      window.removeEventListener('startFocusMode', handleFocusMode);
      window.removeEventListener('exitFocusMode', handleExitFocus);
    };
  }, [currentSprint]);

  // Listen for TactixAI Sprint Card handoff: route to setup so populated fields are visible
  useEffect(() => {
    const onApplySprintCard = () => {
      setCurrentView('setup');
    };
    window.addEventListener('tactixai:apply-sprint-card', onApplySprintCard);
    return () => window.removeEventListener('tactixai:apply-sprint-card', onApplySprintCard);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <LogoTile 
            size="h-16 w-16" 
            cornerRadius="rounded-xl"
            className="mx-auto mb-4"
          />
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen onLogin={login} />;
  }

  const handleStartSprint = (sprintFromSetup) => {
    // Create activeSprint immediately and navigate to timer
    const now = Date.now();
    const activeSprintData = {
      id: sprintFromSetup.id,
      startedAt: now,
      durationMs: sprintFromSetup.duration_minutes * 60 * 1000,
      remainingMs: sprintFromSetup.duration_minutes * 60 * 1000,
      isPaused: true, // Start paused, will be started by handleStartSprint in timer component
      lastTickAt: now,
      tasks: sprintFromSetup.tasks || [],
      goal: sprintFromSetup.goal,
      focusMode: false,
      completed: false,
      break_config: sprintFromSetup.break_config,
      breaksFired: [] // Track which break offsets have been fired
    };
    
    // Persist sprint meta for review screen
    try {
      const meta = {
        goal: (sprintFromSetup.goal || '').trim(),
        durationMin: sprintFromSetup.duration_minutes
      };
      localStorage.setItem('ttx:lastSprintMeta', JSON.stringify(meta));
    } catch (e) {
      // Silent fail - not critical
    }
    
    setActiveSprint(activeSprintData);
    persistActiveSprint(activeSprintData);
    setCurrentSprint(sprintFromSetup);
    setCurrentView('timer');
  };

  const handleCompleteSprint = (sprint) => {
    // Clear activeSprint when sprint completes
    clearActiveSprint();
    
    // Create pendingCompletion for this sprint
    const newPendingCompletion = {
      sprintId: sprint.id,
      goal: sprint.goal,
      durationMinutes: sprint.duration_minutes,
      tasks: sprint.tasks || [],
      timestamp: Date.now(),
      finished: false,
      moodRating: 3,
      proofNote: '',
      winNote: '',
      nextMove: ''
    };
    
    setPendingCompletion(newPendingCompletion);
    persistPendingCompletion(newPendingCompletion);
    
    setCurrentSprint(sprint);
    setCurrentView('completion');
  };

  const handleNewSprint = () => {
    setCurrentSprint(null);
    setCurrentView('setup');
  };

  const handleViewProgress = () => {
    // Pause timer when navigating to Journal using TimerService
    if (activeSprint && !activeSprint.completed && !activeSprint.isPaused) {
      // TimerService.pause() will handle the precise timing and logging
      timerService.pause();
      
      // Update activeSprint state with paused status
      const now = Date.now();
      const currentState = timerService.getState();
      const updatedActiveSprint = {
        ...activeSprint,
        remainingMs: currentState.remainingMs,
        isPaused: true,
        lastTickAt: now
      };
      
      setActiveSprint(updatedActiveSprint);
      persistActiveSprint(updatedActiveSprint);
    }
    
    setCurrentView('progress');
  };

  const handleResumeSprint = async (activeSprintData) => {
    try {
      // Verify activeSprint has valid remaining time
      if (!activeSprintData || activeSprintData.completed || activeSprintData.remainingMs <= 0) {
        console.log('RESUME_NO_STATE', 'No valid activeSprint with remainingMs > 0');
        setCurrentView('setup');
        return;
      }

      // Perform atomic resume transaction
      const updatedActiveSprint = await timerService.resumeAtomic(activeSprintData);
      
      // Update and persist activeSprint
      setActiveSprint(updatedActiveSprint);
      persistActiveSprint(updatedActiveSprint);
      
      // Create sprint object for UI
      const sprint = {
        id: activeSprintData.id,
        goal: activeSprintData.goal,
        duration_minutes: activeSprintData.durationMs / (60 * 1000),
        tasks: activeSprintData.tasks,
        break_config: activeSprintData.break_config
      };
      
      setCurrentSprint(sprint);
      setCurrentView('timer');
      
    } catch (error) {
      console.error('Resume failed:', error);
      console.log('RESUME_NO_STATE', 'Resume attempt failed');
      setCurrentView('setup');
    }
  };

  const handleCompleteLastSprint = () => {
    if (pendingCompletion) {
      // Create a minimal sprint object for the completion form
      // The completion form doesn't need full sprint data, just the ID
      const sprint = {
        id: pendingCompletion.sprintId,
        goal: pendingCompletion.goal || 'Sprint Goal',
        duration_minutes: pendingCompletion.durationMinutes || 60,
        tasks: pendingCompletion.tasks || []
      };
      setCurrentSprint(sprint);
      setCurrentView('completion');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={
            <div>
              {currentView === 'setup' && (
                <SprintSetup 
                  onStartSprint={handleStartSprint} 
                  onViewProgress={handleViewProgress}
                  pendingCompletion={pendingCompletion}
                  onCompleteLastSprint={handleCompleteLastSprint}
                />
              )}
              {currentView === 'timer' && currentSprint && (
                <SprintTimer 
                  sprint={currentSprint} 
                  onComplete={handleCompleteSprint} 
                  focusMode={focusMode}
                  setFocusMode={setFocusMode}
                  onViewProgress={handleViewProgress}
                  onSetActiveSprint={(activeSprintData) => {
                    setActiveSprint(activeSprintData);
                    persistActiveSprint(activeSprintData);
                  }}
                  activeSprint={activeSprint}
                  pendingCompletion={pendingCompletion}
                  onCompleteLastSprint={handleCompleteLastSprint}
                />
              )}
              {currentView === 'completion' && currentSprint && (
                <SprintCompletion 
                  sprint={currentSprint} 
                  onNewSprint={handleNewSprint} 
                  onViewProgress={handleViewProgress}
                  pendingCompletion={pendingCompletion}
                  onSavePendingCompletion={(data) => {
                    setPendingCompletion(data);
                    persistPendingCompletion(data);
                  }}
                  onClearPendingCompletion={clearPendingCompletion}
                />
              )}
              {currentView === 'progress' && (
                <ProgressDashboard 
                  onNewSprint={handleNewSprint} 
                  onLogout={logout} 
                  onResumeSprint={handleResumeSprint}
                  activeSprint={activeSprint}
                  pendingCompletion={pendingCompletion}
                  onCompleteLastSprint={handleCompleteLastSprint}
                />
              )}
            </div>
          } />
        </Routes>
        
        {/* Root-level BreakUIController - always mounted across all views */}
        <BreakUIController 
          timer={timerService}
          sprint={activeSprint}
          activeSprint={activeSprint}
          focusMode={focusMode}
        />

        {/* TactixAI collapsible chat panel - available across all authenticated views, hidden in Focus Mode */}
        {!focusMode && <TactixAIChat />}
      </BrowserRouter>
    </div>
  );
}

export default App;